// CARO. Casa de estilo — UI translations (ES + EN here, FR in dictionary.fr.ts).
// This dictionary covers the full customer-facing interface; every locale must
// carry exactly the ES key set (enforced by tests/dictionary.test.ts). Long-form
// legal text lives in src/content/legal*.ts, not here.

import type { Locale } from '@/types';
import { LOCALES } from '@/config/site';
import { fr } from '@/client/i18n/dictionary.fr';

export type UILocale = Locale;

export const UI_LOCALES: UILocale[] = LOCALES;

export type Dict = Record<string, string>;

const es: Dict = {
  announcement:
    'Envío gratuito en la eurozona desde 50 € (solo belleza y estilo) · Selección personal de CARO',
  // Temporary fuel surcharge (FUEL_SURCHARGE_PERCENT > 0). No carrier figures, no «multa».
  'announcement.fuel': 'Envíos con recargo temporal por combustible',
  'announcement.fuelMore': 'Más información sobre el recargo de envío',
  'announcement.fuelInfo':
    'Con la subida del combustible, los transportistas han encarecido sus tarifas. Mientras dure, el envío lleva un recargo temporal que verás desglosado al pagar. El envío gratuito en la eurozona desde 50 € se mantiene. Lo retiraremos en cuanto el transporte vuelva a la normalidad.',

  'nav.shop': 'Shop',
  'nav.theEdit': 'Edit',
  'nav.journal': 'Edit',
  'nav.caro': 'CARO',
  'nav.instagram': 'Instagram',
  'nav.contact': 'Contacto',
  'nav.beauty': 'Belleza',
  'nav.casa': 'Casa',
  'nav.estilo': 'Estilo',
  'nav.finds': 'Hallazgos',
  'nav.all': 'Todo',
  'nav.about': 'Sobre CARO',
  'nav.philosophy': 'Filosofía',
  'nav.services': 'Servicios',
  'nav.newsletter': 'La carta de CARO',

  'a11y.skip': 'Saltar al contenido',
  'a11y.menuOpen': 'Abrir menú',
  'a11y.menuClose': 'Cerrar menú',
  'a11y.cart': 'Carrito',
  'a11y.breadcrumb': 'Migas de pan',
  'a11y.homeLink': 'CARO. Casa de estilo, inicio',
  'a11y.mainNav': 'Principal',
  'a11y.sectionsNav': 'Secciones',
  'a11y.menu': 'Menú',
  'a11y.qtyDecrease': 'Reducir',
  'a11y.qtyIncrease': 'Aumentar',

  'common.addToCart': 'Añadir',
  'common.enquire': 'Solicitar',
  'common.onRequest': 'Consultar',
  'common.priceTbc': 'Precio por confirmar',
  'common.unverified': 'Ficha por verificar',
  'common.home': 'Inicio',
  'common.viewBeauty': 'Ver belleza',
  'common.discoverCasa': 'Descubrir Casa',
  'common.readMore': 'Leer más',
  'common.loading': 'Cargando…',
  'common.language': 'Idioma',
  'common.keepExploring': 'Seguir explorando',
  'common.retry': 'Reintentar',
  'common.loadError': 'No se ha podido cargar la página.',
  'common.backHome': 'Volver al inicio',
  'common.name': 'Nombre',
  'common.email': 'Correo',
  'common.message': 'Mensaje',
  'common.sending': 'Enviando…',
  'common.sendError': 'No se pudo enviar. Inténtalo de nuevo.',
  'common.hours': 'Horario: lunes a sábado, de 10:00 a 17:00. Otros horarios, con cita previa.',

  'states.errorTitle': 'Algo no ha ido bien',
  'states.errorMsg': 'Vuelve a intentarlo en unos instantes.',

  'home.heroStatement': 'Una casa curada de estilo, belleza y bienestar.',
  'home.ctaPrimary': 'Descubrir la selección',
  'home.ctaSecondary': 'Descubre CARO',
  'home.closing.line1': 'Estilo para mujeres que evolucionan.',
  'home.closing.line2': 'Belleza y bienestar con la mirada de Carolina.',
  'home.closing.signature': 'CARO SIGNATURE. Mi sello. Tu elección.',
  'home.introEyebrow': 'La mirada de CARO',
  'home.introTitle': 'Vestirse es solo el principio.',
  'home.introBody':
    'Vamos por LA ELEGANCIA QUE DEJA HUELLA.\n\nLa moda cambia. La vida también.\n\nSABER ELEGIR\n Entender qué merece entrar en tu armario, tu neceser y tu casa.\n\nSABER LLEVAR\n Porque una prenda, un perfume o un accesorio cambian según quién los lleva. \n\nSABER ESTAR\nPresencia, carácter, educación, cultura y esa elegancia que no depende de lo que llevas puesto. La verdadera presencia empieza donde termina el espejo. La imagen llama la atención. La manera de estar deja huella.',
  'home.introTodo': 'CARO | Casa de estilo es para mujeres que no quieren seguirlo todo, sino aprender a elegir y a IDENTIFICAR lo que realmente las representa.',
  'home.aboutLink': 'Sobre CARO →',
  'home.editorialEyebrow': 'Nuestra manera de mirar la belleza',
  'home.editorialTitle': 'Menos productos. Mejores rituales.',
  'home.editorialBody':
    'Creemos en rutinas simples y efectivas, pensadas para una piel que evoluciona. Selección intencional, ingredientes que cumplen y el placer de un gesto diario. Sin promesas imposibles.',
  'home.casaEyebrow': 'Casa',
  'home.casaTitle': 'Objetos de la casa de CARO',
  'home.casaLede': 'Arte y diseño de coleccionista. Piezas seleccionadas, a menudo únicas.',
  'home.preparing': 'Preparando la selección…',

  // Poster hero (editorial cover, replicates the printed portada)
  'home.poster.brandSub': 'Casa de estilo',
  'home.poster.tagline': 'Estilo · Belleza · Presencia · Casa',
  'home.poster.title': 'Vestirse es solo el principio.',
  'home.poster.subLead': 'Para mujeres que no quieren seguirlo todo, sino aprender a elegir ',
  'home.poster.subAccent': 'lo que realmente las representa.',
  'home.poster.needTo': 'Saber',
  'home.poster.choose': 'Elegir',
  'home.poster.wear': 'Llevar',
  'home.poster.be': 'Estar',
  'home.poster.byLabel': 'Por',
  'home.poster.byName': 'Carolina Collaer',
  'home.poster.closing': 'Tu estilo puede verse. Tu presencia debe sentirse.',
  // Home "who is CARO" block (between the stores and the Edit): H2 carries the local-SEO terms.
  // `{key|text}` = inline link (src/lib/inlineLinks.ts): services · shop · instagram · letter.
  'home.about.eyebrow': 'Quién es CARO',
  'home.about.title': 'Asesoría de imagen en Sitges, Garraf, Penedès y Barcelona',
  'home.about.body1':
    'Soy Carolina Collaer, {services|asesora de imagen y personal shopper}. Te acompaño a elegir lo que de verdad te representa, en sesiones presenciales u online.',
  'home.about.body2':
    'También puedes descubrir {shop|mi tienda online}, con selecciones exclusivas de moda, belleza y casa, {instagram|seguirme en Instagram} o quedarte informada con {letter|La carta de CARO}.',

  'ig.title': 'En Instagram',
  'ig.altFallback': 'Publicación de Instagram de Carolina Collaer',
  'testimonials.eyebrow': 'Confianza',
  'testimonials.title': 'Lo que dicen',
  'testimonials.item': 'Testimonio',
  'testimonials.placeholder': '(placeholder)',

  'newsletter.eyebrow': 'Comunidad',
  'newsletter.title': 'Recibe la Carta de CARO.',
  'newsletter.body': 'Una vez al mes, recibirás esta carta con ideas, hallazgos, lo nuevo por llegar a CARO y pequeños secretos sobre estilo, belleza, presencia y casa. Una carta para inspirarte, descubrir y elegir mejor.',
  'newsletter.placeholder': 'tu@correo.com',
  'newsletter.submit': 'Suscribirme',
  'newsletter.sending': 'Enviando…',
  'newsletter.done': 'Gracias. Revisa tu correo para confirmar.',
  'newsletter.consent': 'Acepto recibir lo que he marcado y la política de privacidad.',
  'newsletter.consentLetter': 'Acepto recibir la carta de CARO y la política de privacidad.',
  'newsletter.consentFinds': 'Acepto recibir los avisos de Hallazgos de CARO y la política de privacidad.',
  'newsletter.listsLegend': 'Qué quieres recibir',
  'newsletter.list.letter': 'La carta de CARO · una vez al mes',
  'newsletter.list.finds': 'Hallazgos · solo cuando llega un drop',
  'newsletter.listsError': 'Elige al menos una: La carta o Hallazgos.',
  'newsletter.error': 'Revisa el correo y marca el consentimiento.',
  'newsletter.honeypot': 'No rellenar',

  // /newsletter page (the shared Newsletter form sits below this copy)
  'newsletterPage.eyebrow': 'Comunidad',
  'newsletterPage.title': 'Recibe la Carta de CARO.',
  'newsletterPage.lede': 'Una vez al mes, recibirás esta carta con ideas, hallazgos, lo nuevo por llegar a CARO y pequeños secretos sobre estilo, belleza, presencia y casa.',
  'newsletterPage.p1': 'Una carta para inspirarte, descubrir y elegir mejor.',
  'newsletterPage.p2': 'Y si te gustan las piezas únicas, apúntate también a Hallazgos: solo te escribo cuando llega un drop.',
  'newsletterPage.signoff': 'Con cariño,',
  'newsletterPage.signature': 'Carolina Collaer',
  'newsletterPage.imageAlt': 'Carolina Collaer, fundadora de CARO. Casa de estilo',
  'newsletterPage.formTitle': 'Forma parte de CARO.',
  'newsletterPage.formBody': 'Déjame tu correo y confirma tu suscripción en el enlace que recibirás.',
  'newsletterPage.unsubTitle': '¿Quieres darte de baja?',
  'newsletterPage.unsubBody':
    'Cada carta lleva al final un enlace para darte de baja en un clic, cuando quieras. Si no lo encuentras, escríbeme y te doy de baja yo.',
  'newsletterPage.unsubLink': 'Pedir la baja por email',
  'newsletterPage.unsubSubject': 'Baja de La carta de CARO',
  'newsletterPage.privacyLead': 'Tus datos se tratan según nuestra',
  'newsletterPage.privacyLink': 'política de privacidad',
  'newsletterPage.archiveTitle': 'Cartas enviadas',
  'letter.formTitle': '¿Quieres recibir la próxima?',
  'letter.readArticle': 'Leer el artículo',
  'letter.notFoundTitle': 'Carta no encontrada',
  'letter.notFoundMsg': 'Puede que esta carta aún no se haya enviado.',

  'cart.title': 'Tu selección',
  'cart.empty': 'Tu carrito está vacío.',
  'cart.subtotal': 'Subtotal',
  'cart.note': 'Envío e impuestos calculados en el pago.',
  'cart.checkout': 'Tramitar pedido',
  'cart.remove': 'Quitar',
  'cart.size': 'Talla',
  'cart.pageTitle': 'Carrito',
  'cart.emptyTitle': 'Tu carrito está vacío',
  'cart.emptyMsg': 'La selección de CARO te espera.',
  'cart.discoverBeauty': 'Descubrir belleza',

  'footer.explore': 'Explorar',
  'footer.info': 'Información',
  'footer.tagline': 'Estilo para mujeres que evolucionan.',
  'footer.tagline2': 'Belleza y bienestar con la mirada de Carolina.',
  'footer.signature': 'CARO SIGNATURE. Mi sello. Tu elección.',
  'footer.prices': 'Precios en EUR con IVA incluido cuando corresponda.',
  'footer.madeBy': 'Web realizada por',
  'footer.legal.notice': 'Aviso legal',
  'footer.legal.privacy': 'Privacidad',
  'footer.legal.cookies': 'Cookies',
  'footer.legal.terms': 'Condiciones de compra',

  'shop.hubEyebrow': 'Selección CARO',
  'shop.hubTitle': 'Selección de CARO',
  'shop.hubCta': 'Descubrir',
  'shop.hubIntro':
    'La selección personal de CARO: estilo, belleza y objetos de diseño elegidos con criterio. Y, para acompañarte, la asesoría de imagen de Carolina.',
  'shop.beauty.title': 'Belleza',
  'shop.beauty.eyebrow': 'Belleza · Selección CARO',
  'shop.beauty.lede': 'Cosmética seleccionada para la piel que evoluciona.',
  'shop.beauty.intro':
    '## Belleza con criterio.\n\n' +
    'Menos promesas. Mejores elecciones.\n\n' +
    'La belleza también evoluciona con nosotras. Cambia la piel, cambian nuestras necesidades y cambia, sobre todo, la forma en que queremos cuidarnos.\n\n' +
    'En CARO entendemos la belleza como una elección consciente: conocer mejor lo que necesitamos, aprender a distinguir lo esencial de lo accesorio y rodearnos de productos que aporten calidad, placer y sentido a nuestros rituales cotidianos.\n\n' +
    '## Pocas fórmulas, bien elegidas\n\n' +
    'Esta selección no busca acumular novedades ni seguir la última tendencia. Reúne sérums, protección solar, contorno de ojos y mascarillas de marcas como Meisani y Ondo Beauty 36.5, elegidos por Carolina para acompañar a la piel que evoluciona.\n\n' +
    'En cada ficha encontrarás qué aporta el producto y cómo usarlo, para que decidas con información y sin prisas.\n\n' +
    '## Saber elegir también es esto\n\n' +
    'Una rutina no mejora por tener más pasos. Mejora cuando cada gesto tiene sentido para ti y lo mantienes con constancia. En el Edit hablamos de belleza desde la imagen y el cuidado: cómo ordenar una rutina, qué preguntas hacerse antes de comprar y cómo reconocerse sin querer corregirse.',
  'shop.casa.title': 'Casa',
  'shop.casa.eyebrow': 'Arte y diseño de coleccionista',
  'shop.casa.lede': 'Objetos con personalidad, a menudo piezas únicas. Descubre el universo de CARO.',
  'shop.casa.intro':
    '## La casa también habla de ti.\n\n' +
    'Los objetos que eliges, el arte que te acompaña, una lámpara, un libro o una pieza especial construyen la identidad de tu hogar.\n\n' +
    'No se trata de llenar espacios, sino de saber elegir.\n\n' +
    '## Piezas con historia\n\n' +
    'Casa reúne obras y objetos con personalidad, a menudo piezas únicas: las esculturas originales de NAOR o el diseño de autor de Hamed Ouattara, entre otros. Carolina los elige por su mirada, su oficio y la historia que cuentan, no por la tendencia del momento.\n\n' +
    'Son piezas para convivir con ellas durante años, no para cambiar cada temporada. Por eso merece la pena elegirlas con calma.\n\n' +
    '## Antes de decidir\n\n' +
    'Si una pieza te interesa y prefieres consultar antes de comprar, en la ficha de muchas de ellas puedes solicitar información. Te responderemos personalmente.\n\n' +
    '## Una casa que se parece a ti\n\n' +
    'Cada objeto que eliges cuenta algo de quien vive con él. En el Edit hablamos de arte, de diseño y de cómo convivir con las piezas que elegimos.',
  'shop.estilo.title': 'Estilo',
  'shop.hallazgos.title': 'Hallazgos',
  'shop.estilo.eyebrow': 'Estilo · Selección CARO',
  'shop.estilo.lede': 'La moda propone. CARO selecciona. Tú decides.',
  'shop.estilo.intro':
    '## En esta sección encontrarás lo que merece quedarse.\n\n' +
    'Porque un buen bolso, unas gafas con presencia, una chaqueta impecable o el accesorio adecuado no son simplemente complementos. Son decisiones. Y cuando están bien elegidas, construyen una imagen coherente sin necesidad de excesos.\n\n' +
    '## Menos piezas, mejor elegidas\n\n' +
    'En Estilo reunimos prendas y accesorios que Carolina elige con la pregunta que guía todo CARO: ¿tiene sentido para ti? Punto, cazadoras, bolsos y joyas de piedra natural de firmas como Surkana, junto al brazalete CARO.\n\n' +
    'No se trata de renovar el armario cada temporada, sino de sumar lo que dialoga con lo que ya tienes y con quien eres hoy.\n\n' +
    '## Saber llevar\n\n' +
    'Una prenda, una joya o un color nunca cuentan exactamente la misma historia en dos personas. En el Edit compartimos ideas para llevar cada pieza a tu manera: proporciones, combinaciones y esos pequeños detalles capaces de transformar una imagen.\n\n' +
    'El estilo aparece cuando la referencia termina y empieza tu interpretación.\n\n' +
    'La moda propone. CARO selecciona. Tú decides.',
  'shop.servicios.title': 'Asesoría de imagen',
  'shop.servicios.eyebrow': 'Con Carolina',
  'shop.servicios.lede': 'Acompañamiento personal en imagen, estilo y presencia.',
  'shop.allTitle': 'Todo',
  'shop.allEyebrow': 'Selección CARO',
  'shop.allLede': 'La selección completa de CARO. Casa de estilo.',
  'shop.emptyTitle': 'Aún no hay piezas aquí',
  'shop.emptyMsg': 'Vuelve pronto: la selección se está preparando.',

  'finds.title': 'Hallazgos',
  'finds.eyebrow': 'Estilo · Piezas únicas',
  'finds.lede': 'Lo que Carolina encuentra en tiendas, viajes y rebajas. Hay una de cada.',
  'finds.intro':
    '## Una de cada\n\n' +
    'Cada hallazgo es una pieza que Carolina ha encontrado y elegido para ti: en una pequeña tienda, de viaje o en la última percha de las rebajas. No hay reposición. Cuando se va, se va.\n\n' +
    '## Cómo elige\n\n' +
    'Calidad visible, piezas que duran y que dialogan con lo que ya tienes, elegidas una a una.',
  'finds.newsletterCta': 'También puedes recibir La carta de CARO, una vez al mes',
  'finds.letterEyebrow': 'Aviso de drop, en directo',
  'finds.letterTitle': 'Entérate en el momento en que llega un hallazgo.',
  'finds.letterBody': 'Te escribo en cuanto se publica un drop, no después: piezas únicas, una de cada, y quien llega primero se la lleva. Ningún otro correo.',
  'finds.emptyTitle': 'El próximo hallazgo está en camino',
  'finds.emptyMsg': 'Carolina está buscando. Apúntate a los avisos de Hallazgos y te escribo cuando llegue el próximo drop.',
  'finds.emptyCta': 'Ver todas las suscripciones',
  'finds.teaserEyebrow': 'Hallazgos',
  'finds.teaserText': 'Piezas únicas que Carolina encuentra en tiendas, viajes y rebajas. Hay una de cada.',
  'finds.teaserCta': 'Ver los hallazgos',
  'finds.story': 'La historia',
  'finds.measurements': 'Medidas',
  'finds.condition': 'Estado',
  'finds.condition.new_with_tags': 'Nuevo con etiqueta',
  'finds.condition.new': 'Nuevo',
  'finds.condition.excellent': 'Excelente estado',
  'finds.condition.good': 'Buen estado',

  'pdp.unverifiedLong': 'Ficha por verificar. Información oficial pendiente',
  'pdp.addToSelection': 'Añadir al carrito',
  'pdp.officialInfo': 'Información del producto',
  'pdp.spec.size': 'Formato',
  'pdp.spec.benefits': 'Beneficios',
  'pdp.spec.howToUse': 'Modo de uso',
  'pdp.spec.ingredients': 'Ingredientes',
  'pdp.spec.inci': 'INCI',
  'pdp.spec.edition': 'Edición',
  'pdp.disclaimer':
    'La información de ingredientes e INCI oficial prevalece sobre cualquier nota editorial. No constituye consejo médico.',
  'pdp.notFoundTitle': 'Producto no encontrado',
  'pdp.notFoundMsg': 'Puede que esta pieza ya no esté disponible.',
  'pdp.enquireTitle': 'Solicitar información',
  'pdp.enquireAlso': '¿Prefieres consultar antes de comprar? Solicita información',
  'pdp.fuelShipping': 'Envío con el recargo temporal por combustible:',
  'pdp.zoneSpain': 'España',
  'pdp.zoneEurope': 'Europa',
  'pdp.zoneInternational': 'Internacional',
  'pdp.perPiece': ' (por pieza).',
  // Delivery timings: numbers come from SHIPPING_TIMES (formatDeliveryEstimate), never from here.
  'shipping.estimate.handling': 'Preparación {min}-{max} días laborables',
  'shipping.estimate.delivery': 'entrega:',
  'shipping.estimate.zone.es': 'España {min}-{max}',
  'shipping.estimate.zone.europe': 'Europa {min}-{max}',
  'shipping.estimate.zone.international': 'internacional {min}-{max}',
  'shipping.estimate.total': 'Entrega estimada: {min}-{max} días laborables',
  'pdp.enquireThanks': 'Gracias por tu interés. Te responderemos personalmente lo antes posible.',
  'pdp.sendRequest': 'Enviar solicitud',
  'pdp.soldOutTitle': 'Agotado',
  'pdp.soldPieceTitle': 'Pieza vendida',
  'pdp.soldPieceText':
    'Esta pieza única ya ha encontrado su casa. Si buscas algo con el mismo espíritu, la selección sigue creciendo.',
  'pdp.soldPieceLetterTitle': 'Descubre antes que nadie lo que llega a CARO.',
  'pdp.soldFindLetterTitle': 'Que no se te escape el próximo hallazgo.',
  'pdp.alternatives': 'Ver la selección de {category}',
  'pdp.unavailable': 'No disponible por ahora.',
  'restock.title': 'Avísame cuando vuelva',
  'restock.body': 'Déjanos tu correo y te escribiremos una sola vez, cuando vuelva a estar disponible.',
  'restock.submit': 'Avisarme',
  'restock.consent': 'Acepto que CARO me escriba sobre esta pieza y la política de privacidad.',
  'restock.alsoLetter': 'Recibir también La carta de CARO',
  'restock.done': 'Gracias. Revisa tu correo para confirmar el aviso.',
  'restock.error': 'Revisa el correo y marca el consentimiento.',
  'pdp.available': 'Disponible',
  'pdp.allInCart': 'Ya tienes todas las unidades disponibles en tu selección.',
  'pdp.size': 'Talla',
  'pdp.chooseSize': 'Elige tu talla para añadirla al carrito.',
  // Packs (docs/plan-packs.md)
  'pack.badge': 'Pack −{pct} %',
  'pack.includes': 'Incluye:',
  'pack.contents': 'Qué incluye',
  'pack.savings': 'Ahorras {amount}',
  'pack.unavailable': 'Este pack no está disponible ahora.',
  'pack.unavailableHint': 'Puedes ver cada producto en su ficha.',
  'pack.sizeFor': 'Talla de {name}',
  'pack.chooseSizes': 'Elige una talla para cada producto con talla.',
  'pack.notFoundTitle': 'Pack no encontrado',
  'pack.notFoundMsg': 'Puede que este pack ya no esté disponible.',
  'pack.metaTitle': 'Pack',
  'checkout.packIssueTitle': 'Un pack de tu carrito no está disponible',
  'checkout.packIssueBody': 'Puede que algún producto se haya agotado o que falte elegir una talla. Revisa el pack o quítalo para continuar.',
  'checkout.packIssueView': 'Ver el pack',
  'pdp.sizeSoldOut': 'Agotada',
  'pdp.preorderNotice':
    'En reasortimiento: estamos recibiendo esta pieza, así que la entrega llevará un poco más de tiempo.',
  'product.preorderBadge': 'En reasortimiento',
  'product.editTitle': 'En el Edit',
  'pdp.caroNote': 'Por qué lo elige CARO',

  'checkout.eyebrow': 'Pago seguro',
  'checkout.title': 'Finalizar pedido',
  'checkout.contact': 'Datos de contacto',
  'checkout.phone': 'Teléfono (opcional)',
  'checkout.email': 'Correo electrónico (obligatorio)',
  'checkout.emailHint': 'Te enviaremos la confirmación y el seguimiento del envío.',
  'checkout.firstName': 'Nombre',
  'checkout.lastName': 'Apellidos',
  'checkout.shipping': 'Envío',
  'checkout.address': 'Dirección',
  'checkout.city': 'Ciudad',
  'checkout.zip': 'Código postal',
  'checkout.province': 'Provincia',
  'checkout.country': 'País',
  'checkout.pay': 'Ir a pagar con SumUp',
  'checkout.preparing': 'Preparando el pago…',
  'checkout.secureNote':
    'El precio, IVA, existencias y totales se calculan de forma segura en el servidor. El pago se procesa con SumUp; CARO no almacena datos de tarjeta.',
  'checkout.summary': 'Tu selección',
  'checkout.shippingCalc': 'Envío calculado en el siguiente paso según destino.',
  'checkout.vat': 'IVA incluido',
  'checkout.shippingLabel': 'Envío',
  'checkout.total': 'Total',
  'checkout.free': 'Gratis',
  'checkout.fuelSurcharge': 'Recargo temporal combustible',
  'checkout.calculating': 'Calculando…',
  'checkout.shippingPending': 'Se calcula con tu dirección',
  'checkout.shippingNote2': 'El envío depende del destino y se confirma con tu dirección.',
  'checkout.customsNote': 'Los envíos fuera de la Unión Europea pueden estar sujetos a aranceles e impuestos de aduana, a cargo de la persona compradora.',
  'checkout.emptyTitle': 'Tu carrito está vacío',
  'checkout.emptyMsg': 'Añade una pieza de la selección para continuar.',
  'checkout.widgetNote':
    'El pago con widget requiere activación de SumUp. Cambia SUMUP_CHECKOUT_MODE a "hosted" para probar el flujo completo.',
  'checkout.genericError': 'No se pudo iniciar el pago. Inténtalo de nuevo.',
  // API error codes (ES = the Worker's own message, routes/checkout.ts)
  'checkout.error.invalidRequest': 'Datos de pedido no válidos.',
  'checkout.error.cartInvalid': 'Revisa tu carrito.',
  'checkout.error.turnstile':
    'Verificación anti-robot fallida. Recarga la página e inténtalo de nuevo.',
  'checkout.manualTitle': 'Gestionaremos tu pedido por correo',
  'checkout.manualBody':
    'No hemos podido verificar tu dirección automáticamente, así que resolveremos tu pedido personalmente por correo. No se ha realizado ningún cobro y el stock no queda garantizado hasta confirmar. Referencia:',
  'checkout.addrSuggestTitle': '¿Quisiste decir esta dirección?',
  'checkout.addrSuggestBody': 'Hemos encontrado una dirección similar. Revísala y aplícala si es correcta:',
  'checkout.addrUse': 'Usar esta dirección',
  'checkout.addrKeep': 'Mantener la mía y enviar como consulta',
  'checkout.addrNotFoundTitle': 'No encontramos tu dirección',
  'checkout.addrNotFoundBody': 'Revísala e inténtalo de nuevo, o envíala como consulta para gestión manual (sin pago, stock no garantizado).',
  'checkout.addrRetry': 'He revisado, reintentar',
  'checkout.addrSendManual': 'Enviar como consulta',
  'checkout.address2': 'Complemento opcional (piso, puerta, escalera…)',
  'checkout.addrHelp': 'Confirma tu dirección para asegurar la entrega antes de pagar.',
  'checkout.validate': 'Validar dirección',
  'checkout.addrChecking': 'Comprobando…',
  'checkout.addrVerified': 'Dirección verificada ✓',
  'checkout.addrNone': 'No encontramos esta dirección. Revísala o continúa como consulta.',
  'checkout.addrChoose': 'Elige tu dirección:',
  'checkout.addrCheckError': 'No se pudo validar ahora. Puedes continuar igualmente.',
  'checkout.continue': 'Continuar',
  'checkout.reviewTitle': 'Revisa tu pedido',
  'checkout.reviewEyebrow': 'Confirmación',
  'checkout.reviewIntro': 'Comprueba que todo es correcto antes de pasar al pago.',
  'checkout.reviewContact': 'Contacto',
  'checkout.reviewShipTo': 'Enviar a',
  'checkout.reviewCost': 'Detalle de coste',
  'checkout.reviewNoPhone': 'Sin teléfono',
  'checkout.confirmPay': 'Confirmar y pagar',
  'checkout.editOrder': 'Volver a editar',
  'checkout.acceptPre': 'Acepto las',
  'checkout.acceptTermsLink': 'Condiciones de compra',
  'checkout.acceptMid': 'y he leído la',
  'checkout.acceptPrivacyLink': 'Política de privacidad',
  'checkout.reviewShippingPending': 'Se confirma al pagar',
  'checkout.soldOutTitle': 'Lo sentimos, se acaba de agotar',
  'checkout.soldOutBody':
    'La última pieza acaba de venderse mientras terminabas el pedido. Sentimos mucho la coincidencia.',
  'checkout.soldOutContact': '¿Quieres que te avisemos si vuelve? Escríbenos.',
  'checkout.soldOutBack': 'Volver a la tienda',
  'checkout.backorderTitle': 'En reasortimiento: entrega con demora',
  'checkout.backorderBody':
    'Estos artículos ya no están en stock. La entrega puede tardar más de lo habitual, normalmente entre 1 y 2 semanas. Como el pedido se envía junto, todo el pedido llegaría con esa demora.',
  'checkout.backorderContinue': 'Continuar de todas formas',
  'checkout.backorderRemove': 'Quitar',
  'checkout.sizeRequiredTitle': 'Elige una talla',
  'checkout.sizeRequiredBody':
    'Estas prendas ahora se venden por tallas. Elige la tuya en la ficha del producto o quítala del carrito para continuar.',
  'checkout.sizeRequiredFor': 'Elige una talla para',
  'checkout.sizeRequiredChoose': 'Elegir talla',

  'return.verifying': 'Confirmando tu pago…',
  'return.thanks': 'Gracias',
  'return.paidTitle': 'Pedido confirmado',
  'return.paidBody1': 'Hemos recibido tu pago. Tu número de pedido es',
  'return.paidBody2': 'Te enviaremos la confirmación por correo si lo has facilitado.',
  'return.pendingTitle': 'Pago en verificación',
  'return.pendingBody1': 'Tu pago aún se está confirmando. Guarda tu número de pedido',
  // Starts with the separator after the order number (French spaces it: ' ;').
  'return.pendingBody2': '; te avisaremos cuando se complete.',
  'return.failedTitle': 'El pago no se completó',
  'return.failedBody': 'No se ha realizado ningún cargo. Puedes volver a intentarlo.',
  'return.errorTitle': 'No pudimos verificar el pedido',
  'return.errorBody': 'Si crees que el pago se realizó, contáctanos con tu número de pedido.',
  'return.contact': 'Contacto',

  'about.eyebrow': 'Sobre CARO',
  'about.portrait': 'Retrato de Carolina',
  'about.title': 'Todo empezó con una pregunta.',
  'about.lede':
    '¿Por qué algunas personas tienen presencia incluso antes de hablar?',
  'about.story1':
    'Soy Carolina Collaer. Durante años he trabajado alrededor de la imagen, la comunicación, el arte, la moda y el diseño.',
  'about.story2':
    'Con el tiempo entendí que todas esas disciplinas hablaban, en el fondo, de algo parecido: cómo elegimos presentarnos ante el mundo.',
  'about.story3':
    'CARO. Casa de estilo nace de esa mirada. No para decirte qué debes llevar. Para compartir lo que he aprendido, seguir aprendiendo contigo y ayudarte a desarrollar algo mucho más interesante que un armario perfecto: tu propio criterio.',
  'about.tags': 'Asesora de imagen · Moda y lujo · Arte y lifestyle · ES · FR · EN',
  'about.followIg': '¿Conectamos en Instagram?',

  'philosophy.title': 'Filosofía',
  'philosophy.lede': 'Estilo para la mujer que evoluciona.',
  'philosophy.body':
    'Creemos que la belleza, el estilo, el bienestar y la presencia forman una misma conversación. Y que la curaduría vale más que el catálogo.\n\n' +
    'Todo empezó con una pregunta: ¿por qué algunas personas tienen presencia incluso antes de hablar? CARO. Casa de estilo nace de esa mirada. No para decirte qué debes llevar, sino para ayudarte a desarrollar algo mucho más interesante que un armario perfecto: tu propio criterio.\n\n' +
    '## Saber elegir\n\n' +
    'Aprender a distinguir entre lo que simplemente atrae nuestra mirada y aquello que verdaderamente nos representa. Elegir mejor no significa tener menos posibilidades. Significa tener más criterio.\n\n' +
    '## Saber llevar\n\n' +
    'Una prenda, un perfume, una joya o un color nunca cuentan exactamente la misma historia en dos personas. El estilo aparece cuando la referencia termina y empieza tu interpretación.\n\n' +
    '## Saber estar\n\n' +
    'La presencia no se construye solamente con aquello que se ve. La imagen puede llamar la atención; el comportamiento es lo que termina dejando huella.\n\n' +
    '## Estilo · Belleza · Bienestar\n\n' +
    'Una casa para la mujer que evoluciona, con la mirada de Carolina. La moda propone. CARO selecciona. Tú decides.\n\n' +
    'No pretendemos establecer reglas universales ni decirte cómo debes ser. Queremos ofrecerte referencias, conocimiento y preguntas que te permitan observar mejor, cuestionar más y decidir por ti misma.\n\n' +
    'Porque cuanto más conoces, menos necesitas seguir.\n\n' +
    '**SABER ELEGIR · SABER LLEVAR · SABER ESTAR**',

  'services.colorimetry.eyebrow': 'Test de colorimetría gratis',
  'services.colorimetry.text': 'Antes de reservar, descubre tu estación de color con tu pelo, tus ojos y tu piel, llévate tu ficha PDF personalizada, gratis y anónima, y juega para comprobar que entiendes tu paleta.',
  'services.colorimetry.cta': 'Descubrir mi colorimetría',
  'services.colorimetry.imageAlt': 'COLORIMETRY ME!: croquis de moda vestido con colores de temporada y una muestra de paletas',
  'services.silhouette.eyebrow': 'Test de silueta gratis',
  'services.silhouette.title': 'Tu silueta',
  'services.silhouette.text': 'Responde 4 preguntas frente al espejo, sin medidas ni fotos, descubre tu silueta y qué prendas la equilibran, llévate tu ficha PDF personalizada, gratis y anónima, y juega a «Elige para el efecto».',
  'services.silhouette.cta': 'Hacer el test de silueta',
  'services.silhouette.imageAlt': 'Test de silueta de CARO: croquis de moda de una silueta en X',
  'services.face.eyebrow': 'Test de forma de cara gratis',
  'services.face.title': 'Tu forma de cara',
  'services.face.text': 'Responde 3 preguntas frente al espejo, sin fotos, descubre tu forma de cara y qué monturas, peinados, accesorios y contouring la acompañan, llévate tu ficha PDF personalizada, gratis y anónima, y prueba monturas en el probador.',
  'services.face.cta': 'Hacer el test de forma de cara',
  'services.face.imageAlt': 'Test de forma de cara de CARO: croquis de un rostro ovalado con gafas de ojo de gato',
  'services.eyebrow': 'Imagen & Presencia',
  'services.title': 'Asesoría de imagen y personal shopper en Sitges, Garraf y Penedès',
  'services.lede': 'Tu imagen no empieza en el armario.',
  'services.manifesto1': 'Empieza por entender quién eres hoy, qué quieres transmitir y qué necesitas realmente.',
  'services.manifesto2':
    'CARO te acompaña a mirar tu imagen con más claridad: reconocer lo que te favorece, entender lo que ya tienes, construir mejores combinaciones y aprender a elegir con más intención.',
  'services.manifesto3': 'No se trata de seguir tendencias, acumular prendas ni convertirte en otra persona.',
  'services.manifesto4': 'Se trata de que tu imagen tenga sentido contigo.',
  'services.mantra': 'Saber elegir. Saber llevar. Saber estar.',
  'services.imageAlt': 'Carolina, asesoría de imagen',

  // Dónde trabajo: the zones themselves come from SERVICE_AREA (config/site.ts).
  'services.area.eyebrow': 'Dónde trabajo',
  'services.area.body': 'Sesiones presenciales en tienda, a domicilio o en el lugar del evento.',
  'services.area.travel': 'Online para cualquier lugar, y desplazamiento nacional o internacional a consultar.',

  // Sub-tabs (subpestañas): personal services vs. brands/stores/groups.
  'services.tabs.label': 'Elige el tipo de asesoría',
  'services.tabs.personal': 'Para ti',
  'services.tabs.pro': 'Marcas, tiendas y grupos',
  'services.tabs.proLede':
    'Experiencias de imagen para boutiques, marcas, hoteles, eventos privados y grupos.',

  // — Para ti —
  'services.svc.sesion.name': 'Sesión CARO · Imagen & Presencia',
  'services.svc.sesion.price': '300 €',
  'services.svc.sesion.meta': '4 horas',
  'services.svc.sesion.body1':
    'Una sesión personal y profunda, en tu casa o en el espacio CARO, trabajando a partir de tu propio armario.',
  'services.svc.sesion.listTitle': 'Analizamos:',
  'services.svc.sesion.list':
    'colores y tonalidades que funcionan contigo\n' +
    'proporciones, siluetas y volúmenes\n' +
    'estilo personal y momento vital\n' +
    'prendas que ya tienes y cómo utilizarlas mejor\n' +
    'accesorios, gafas y pequeños gestos que transforman un look\n' +
    'maquillaje, cuando sea relevante\n' +
    'qué conservar, qué reconsiderar y qué puede faltar realmente',
  'services.svc.sesion.body2':
    'Durante la sesión creamos nuevas combinaciones con tus propias prendas y detectamos las piezas que podrían completar tu armario sin comprar por comprar.',
  'services.svc.sesion.body3':
    'Después recibirás un resumen digital personalizado con las principales conclusiones, combinaciones y recomendaciones trabajadas durante la sesión.',
  'services.svc.sesion.note': 'Disponible también como bono regalo.',

  'services.svc.esencial.name': 'Sesión esencial',
  'services.svc.esencial.price': 'Desde 120 €',
  'services.svc.esencial.meta': '90 minutos',
  'services.svc.esencial.body1':
    'Para cuando no necesitas revisar todo tu armario, pero sí una mirada profesional y concreta.',
  'services.svc.esencial.body2':
    'Puede centrarse en estilo, color, gafas, accesorios, una nueva etapa profesional, un evento, una compra importante o simplemente en resolver aquello que hoy no termina de funcionar en tu imagen.',
  'services.svc.esencial.note': 'Presencial u online.',

  'services.svc.compras.name': 'Personal shopper · Acompañamiento de compras',
  'services.svc.compras.price': 'A partir de 180 €',
  'services.svc.compras.body1': 'Comprar mejor empieza antes de entrar en una tienda.',
  'services.svc.compras.body2':
    'Revisamos previamente qué necesitas, qué merece la pena incorporar y qué deberías dejar de comprar.',
  'services.svc.compras.body3':
    'Después te acompaño en una selección personalizada de prendas, accesorios o gafas, buscando piezas que funcionen con tu vida y con lo que ya tienes, no únicamente con la tendencia del momento.',

  // — Marcas, tiendas y grupos —
  'services.svc.tienda.name': 'Experiencia CARO en tienda',
  'services.svc.tienda.price': 'Desde 300 €',
  'services.svc.tienda.meta': '4 horas',
  'services.svc.tienda.body1':
    'Una experiencia de imagen pensada para boutiques, marcas, hoteles, eventos privados y espacios comerciales.',
  'services.svc.tienda.body2':
    'Podemos crear sesiones breves de color, estilo, accesorios, gafas o maquillaje, con una duración aproximada de 20 minutos por participante.',
  'services.svc.tienda.body3':
    'Una manera diferente de generar conversación, experiencia y relación alrededor del producto.',
  'services.svc.tienda.note': 'El formato se adapta al espacio, al público y al objetivo del evento.',

  'services.svc.privadas.name': 'Experiencias privadas',
  'services.svc.privadas.price': 'Según formato',
  'services.svc.privadas.body1':
    'Cumpleaños, encuentros entre amigas, eventos de empresa o pequeños grupos.',
  'services.svc.privadas.body2':
    'Diseñamos una experiencia alrededor de la imagen, el estilo y la presencia para grupos reducidos.',
  'services.svc.privadas.note': 'Precio según formato y número de participantes.',

  'services.svc.medida.name': 'A medida',
  'services.svc.medida.price': 'Presupuesto personalizado',
  'services.svc.medida.body1': 'Hay momentos que no caben en una fórmula.',
  'services.svc.medida.list':
    'Una revisión puntual del armario.\n' +
    'Preparar la imagen para un cambio profesional.\n' +
    'Encontrar las gafas adecuadas.\n' +
    'Construir looks para un viaje.\n' +
    'Acompañarte en una compra importante.\n' +
    'Una videollamada para resolver una duda concreta.',
  'services.svc.medida.body2': 'Cuéntame qué necesitas y diseñaremos el acompañamiento adecuado.',

  'services.finePrint':
    'Los desplazamientos fuera del área establecida se presupuestan aparte según distancia. Las condiciones y duración de cada servicio se confirman antes de la reserva.',
  'services.formTitle': 'Reserva tu asesoría',
  'services.formCta': 'Solicitar información',
  'services.formThanks': 'Gracias. CARO te escribirá muy pronto para concretar tu asesoría.',

  // Service pages (/caro/servicios/<servicio>, docs/plan-tests-y-servicios.md): AI draft for Carolina.
  'services.page.eyebrow': 'Asesoría de imagen · Sitges, Garraf, Penedès y Barcelona',
  'services.page.includesTitle': 'Qué incluye',
  'services.page.forWhomTitle': 'Para quién',
  'services.page.howTitle': 'Cómo funciona',
  'services.page.priceTitle': 'Precio y duración',
  'services.page.testsTitle': 'Antes de reservar, prueba los tests gratis',
  'services.page.testsText': 'Una primera mirada, gratis y anónima, que luego confirmamos juntas en la sesión.',
  'services.page.othersTitle': 'Otros servicios',
  'services.page.allServices': 'Todos los servicios de asesoría de imagen',
  'services.page.more': 'Ver el detalle',
  'services.testsLink': 'Ver los tres tests de estilo gratis',
  'services.page.sesion.metaTitle': 'Sesión de asesoría de imagen en Sitges',
  'services.page.sesion.metaDescription': 'Sesión CARO de 4 horas: colores, siluetas, armario, gafas y maquillaje con Carolina Collaer, en tu casa en Sitges, Garraf, Penedès y Barcelona.',
  'services.page.sesion.h1': 'Sesión de asesoría de imagen en Sitges, Garraf y Penedès',
  'services.page.sesion.intro': 'La sesión completa de CARO: cuatro horas para mirar con calma tus colores, tu silueta, tu estilo y tu armario, y crear combinaciones nuevas con lo que ya tienes.',
  'services.page.sesion.forWhom': 'Quieres revisar tu imagen a fondo, no solo un detalle\nTienes el armario lleno y aun así no sabes qué ponerte\nEstás en un momento de cambio, profesional o personal\nQuieres comprar menos y mejor',
  'services.page.sesion.how': 'Analizamos tus colores, tus proporciones y tu estilo\nCreamos combinaciones con tus propias prendas y vemos qué podría faltar\nRecibes un resumen digital con las conclusiones y las recomendaciones',
  'services.page.esencial.metaTitle': 'Asesoría de imagen online o presencial',
  'services.page.esencial.metaDescription': 'Sesión esencial de 90 minutos con Carolina Collaer: color, estilo, gafas, accesorios, un evento o una compra importante. Presencial u online.',
  'services.page.esencial.h1': 'Sesión esencial de asesoría de imagen, presencial u online',
  'services.page.esencial.intro': 'Noventa minutos para una mirada profesional y concreta, cuando no necesitas revisar todo tu armario.',
  'services.page.esencial.forWhom': 'Necesitas una respuesta concreta, no una revisión completa\nTienes un evento, una nueva etapa profesional o una compra importante\nQuieres resolver una duda de color, gafas o accesorios',
  'services.page.esencial.how': 'Eliges el tema: estilo, color, gafas, accesorios, un evento o una compra\nNos vemos 90 minutos, en persona u online\nTe llevas respuestas concretas para aplicar desde el día siguiente',
  'services.page.compras.metaTitle': 'Personal shopper en Sitges y Barcelona',
  'services.page.compras.metaDescription': 'Personal shopper con Carolina Collaer: preparamos qué necesitas y te acompaño a elegir prendas, accesorios o gafas que funcionen con tu vida.',
  'services.page.compras.h1': 'Personal shopper en Sitges, Penedès y Barcelona',
  'services.page.compras.intro': 'Comprar mejor empieza antes de entrar en una tienda: primero vemos qué necesitas, después te acompaño a elegirlo.',
  'services.page.compras.forWhom': 'Vas a renovar parte de tu armario y no quieres equivocarte\nCompras mucho y te pones poco\nBuscas una pieza importante: un abrigo, un traje, unas gafas',
  'services.page.compras.how': 'Revisamos qué necesitas y qué no merece la pena comprar\nTe acompaño en una selección personalizada de prendas, accesorios o gafas\nElegimos piezas que funcionan con tu vida y con lo que ya tienes',
  'services.page.tienda.metaTitle': 'Eventos de imagen para tiendas y marcas',
  'services.page.tienda.metaDescription': 'Experiencia CARO en tienda: sesiones breves de color, estilo, gafas o maquillaje para boutiques, marcas, hoteles y eventos, con Carolina Collaer.',
  'services.page.tienda.h1': 'Experiencia de imagen para tiendas, marcas y hoteles',
  'services.page.tienda.intro': 'Una experiencia de imagen que genera conversación y relación alrededor de tu producto, en tu espacio.',
  'services.page.tienda.forWhom': 'Boutiques y marcas que quieren acercar el producto a sus clientes\nHoteles y espacios que buscan una actividad distinta\nEventos privados y comerciales',
  'services.page.tienda.how': 'Adaptamos el formato al espacio, al público y al objetivo\nSesiones breves de unos 20 minutos por participante: color, estilo, accesorios, gafas o maquillaje\nTu equipo y tus clientes viven el producto de otra manera',
  'services.page.privadas.metaTitle': 'Experiencias de imagen para grupos',
  'services.page.privadas.metaDescription': 'Cumpleaños, encuentros entre amigas o eventos de empresa: una experiencia de imagen, estilo y presencia para grupos reducidos, con Carolina Collaer.',
  'services.page.privadas.h1': 'Experiencias privadas de imagen y estilo para grupos',
  'services.page.privadas.intro': 'Una experiencia alrededor de la imagen, el estilo y la presencia, diseñada para tu grupo y tu ocasión.',
  'services.page.privadas.forWhom': 'Cumpleaños y encuentros entre amigas\nEventos de empresa\nPequeños grupos que buscan algo distinto',
  'services.page.privadas.how': 'Nos cuentas la ocasión y cuántas personas sois\nDiseñamos la experiencia a vuestra medida\nEl precio depende del formato y del número de participantes',
  'services.page.medida.metaTitle': 'Asesoría de imagen a medida',
  'services.page.medida.metaDescription': 'Asesoría de imagen a medida con Carolina Collaer: revisar el armario, un cambio profesional, unas gafas, un viaje o una duda concreta. Te lo presupuesto.',
  'services.page.medida.h1': 'Asesoría de imagen a medida',
  'services.page.medida.intro': 'Hay momentos que no caben en una fórmula: diseñamos el acompañamiento que necesitas.',
  'services.page.medida.forWhom': 'Una necesidad concreta que no encaja en las sesiones\nUn viaje, un cambio profesional o unas gafas nuevas\nUna duda que se resuelve en una videollamada',
  'services.page.medida.how': 'Me cuentas qué necesitas\nTe propongo el acompañamiento y el presupuesto\nLo hacemos en persona u online',

  'edit.eyebrow': 'Edit',
  'edit.lede': 'Notas de estilo, belleza y bienestar.',
  'edit.imageAlt': 'Carolina Collaer con gabardina beige y bufanda',
  'journal.introLede': 'Mirar mejor para elegir mejor.',
  'journal.intro':
    '## Saber elegir\n\n' +
    'Aprender a distinguir entre lo que simplemente atrae nuestra mirada y aquello que verdaderamente nos representa.\n\n' +
    'Hablaremos de moda, color, belleza, cosmética, accesorios, perfume, diseño, objetos y tendencias, pero siempre desde una pregunta esencial: ¿tiene sentido para mí?\n\n' +
    'Elegir mejor no significa tener menos posibilidades. Significa tener más criterio.\n\n' +
    '## Saber llevar\n\n' +
    'Porque una prenda, un perfume, una joya o un color nunca cuentan exactamente la misma historia en dos personas.\n\n' +
    'Nos interesa entender las proporciones, los códigos, las combinaciones y esos pequeños detalles capaces de transformar una imagen.\n\n' +
    'Pero también algo más importante: aprender a apropiarnos de lo que llevamos sin convertirnos en una copia de aquello que vemos.\n\n' +
    'El estilo aparece cuando la referencia termina y empieza tu interpretación.\n\n' +
    '## Saber estar\n\n' +
    'Aquí comienza una conversación que para CARO es fundamental.\n\n' +
    'La presencia no se construye solamente con aquello que se ve.\n\n' +
    'También está en la manera de entrar en una habitación, escuchar, conversar, agradecer, responder, compartir lo que sabemos, respetar a los demás, reconocer el talento ajeno o ayudar a otra persona a brillar.\n\n' +
    'Hablaremos de comportamiento, educación, generosidad, carácter, humildad, cultura y códigos sociales porque creemos que todo ello forma parte de la imagen que proyectamos.\n\n' +
    'Puedes vestir impecablemente y, sin embargo, carecer de elegancia.\n\n' +
    'Porque la imagen puede llamar la atención; el comportamiento es lo que termina dejando huella.\n\n' +
    'Editorial quiere abrir conversaciones sobre moda y belleza, pero también sobre edad, identidad, cultura, arte, diseño, relaciones, presencia y esos gestos cotidianos que aparentemente parecen pequeños y, sin embargo, dicen muchísimo de nosotros.\n\n' +
    'No pretendemos establecer reglas universales ni decirte cómo debes ser.\n\n' +
    'Queremos ofrecerte referencias, experiencias, conocimiento y preguntas que te permitan observar mejor, cuestionar más y decidir por ti misma.\n\n' +
    'Porque cuanto más conoces, menos necesitas seguir.\n\n' +
    'Y cuanto más criterio desarrollas, más libre eres para elegir.\n\n' +
    '**SABER ELEGIR · SABER LLEVAR · SABER ESTAR**\n\n' +
    'Leer · observar · aprender · cuestionar · elegir\n\n' +
    'Vestirse es solo el principio.',
  'edit.emptyTitle': 'Pronto habrá más',
  'edit.emptyMsg': 'Estamos preparando el editorial.',
  'edit.cat.estilo': 'Estilo',
  'edit.cat.belleza': 'Belleza',
  'edit.cat.bienestar': 'Bienestar',
  'edit.cat.casa': 'Casa',
  'edit.cat.mujer-40': 'Mujer 40+',
  'edit.shopBeauty': 'Ver la tienda de belleza',
  'edit.shopEstilo': 'Ver la tienda de estilo',
  'edit.shopCasa': 'Ver la tienda de Casa',
  'edit.services': 'Ver la asesoría de imagen',
  'edit.guide': 'Cómo combinar los colores de tu estación',
  // Intro of each Edit category (Markdown): painted under its title and the
  // source of its meta description (lib/seo editCategoryDescription).
  'edit.catIntro.estilo':
    'Estilo no es seguir tendencias: es aprender a elegir lo que de verdad te representa. Prendas, accesorios, color y proporciones, con la mirada de Carolina.\n\n' +
    'Notas para saber elegir y saber llevar, siempre desde una pregunta esencial: ¿tiene sentido para mí? Cómo integrar una pieza de temporada, cómo vestirte para la mujer que eres hoy y cómo sumar sin acumular. Porque el estilo aparece cuando la referencia termina y empieza tu interpretación.',
  'edit.catIntro.belleza':
    'La belleza no consiste en corregirse, sino en reconocerse y saber elegir. Escribimos desde la imagen y el cuidado, no desde la consulta médica.\n\n' +
    'Aquí encontrarás cómo ordenar una rutina con menos productos y bien elegidos, qué preguntas hacerte antes de comprar y cómo acompañar a una piel que cambia con los años. Sin promesas imposibles ni clichés: belleza con criterio, para la mujer que evoluciona.',
  'edit.catIntro.bienestar':
    'Bienestar sin obsesión: cuidarse con calma, sin buscar la perfección ni competir con nadie.\n\n' +
    'Hablamos de pequeños cuidados cotidianos y de esa forma de estar que también forma parte de la imagen que proyectamos. No damos consejos médicos ni recetas universales: compartimos una mirada serena para que elijas lo que tiene sentido para ti. Porque para CARO la belleza, el estilo, el bienestar y la presencia forman una misma conversación.',
  'edit.catIntro.casa':
    'La casa también habla de ti. Decorar bien no es llenar espacios, sino saber qué merece quedarse.\n\n' +
    'Aquí hablamos de arte, diseño y objetos con personalidad, de los creadores que seleccionamos para Casa y de cómo convivir con las piezas que elegimos. Una lámpara, un libro o una escultura también cuentan quién eres. Porque la casa es una extensión del estilo, y en ella también se aprende a saber elegir.',
  'edit.catIntro.mujer-40':
    'Los años no restan posibilidades: ayudan a elegir mejor. Los 40+, una edad para vivir con intención y sin clichés.\n\n' +
    'Aquí hablamos de estilo, belleza y presencia para la mujer que evoluciona: vestirse ya no para parecerse a alguien, sino para reconocerse. Ideas para elegir con más criterio, llevar lo que nos representa y estar en cada etapa con seguridad y naturalidad.',
  'article.notFound': 'Artículo no encontrado',
  'article.productsTitle': 'Selección CARO',
  'share.label': 'Compartir',
  'share.copy': 'Copiar enlace',
  'share.copied': 'Enlace copiado',
  'share.more': 'Más…',

  'contact.eyebrow': 'Hablemos',
  'contact.title': 'Contacto',
  'contact.lede': 'Para consultas de estilo, pedidos, prensa o colaboraciones.',
  'contact.send': 'Enviar mensaje',
  'contact.thanks': 'Gracias por tu mensaje. Te responderemos personalmente.',
  'form.source.label': '¿Cómo nos conociste? (opcional)',
  'form.source.placeholder': 'Elige una opción',
  'form.source.google': 'Google',
  'form.source.maps': 'Google Maps',
  'form.source.instagram': 'Instagram',
  'form.source.referral': 'Recomendación',
  'form.source.event': 'Evento en tienda',
  'form.source.press': 'Prensa',
  'form.source.other': 'Otro',
  'contact.alsoHere': 'También aquí',
  // Labels carry their own colon (French spaces it: ' :').
  'contact.emailLabel': 'Correo:',
  'contact.instagramLabel': 'Instagram:',
  'contact.whatsappCta': 'Escríbeme por WhatsApp',
  'contact.whatsappAria': 'Abrir WhatsApp para escribir a CARO (se abre en una pestaña nueva)',
  'contact.privacyResponsable': 'Responsable:',
  'contact.privacyNote':
    'Tratamos tu nombre y correo únicamente para atender tu consulta (base: tu consentimiento). No los usamos con fines publicitarios ni los cedemos con esa finalidad. Puedes ejercer tus derechos de acceso, rectificación, supresión y oposición. Más información en la',
  'contact.privacyLink': 'Política de privacidad',
  'contact.legalTodo': 'TODO: CONTENT — datos legales de la empresa pendientes.',

  'notfound.eyebrow': 'Error 404',
  'notfound.title': 'Esta página se ha perdido',
  'notfound.body': 'Puede que el enlace haya cambiado. Volvamos a la selección.',

  'consent.title': 'Cookies',
  'consent.body':
    'Usamos cookies necesarias para el funcionamiento del sitio y, con tu permiso, cookies de analítica (Google Analytics) para mejorar la experiencia.',
  'consent.accept': 'Aceptar todo',
  'consent.reject': 'Solo necesarias',
  'consent.more': 'Política de cookies',
  'consent.configure': 'Configurar cookies',

  // Legal pages chrome (the documents themselves live in src/content/legal*.ts)
  'legal.metaTitle': 'Legal',
  'legal.eyebrow': 'Información legal',
  'legal.notFound': 'Documento no encontrado',
  'legal.draftBadge': 'Borrador. Pendiente de revisión legal profesional',
  'legal.draftNote':
    'Este documento es un borrador. Los datos identificativos son reales; el articulado debe ser revisado y validado por un profesional conforme a la normativa española y de la Unión Europea antes de su publicación definitiva.',

  // <head> titles of non-indexed or loading pages (indexable pages: lib/seo/pages.ts)
  'meta.checkout.title': 'Pago',
  'meta.checkoutReturn.title': 'Confirmación',
  'meta.notFound.title': 'Página no encontrada',
  'meta.product.title': 'Producto',
  'meta.article.title': 'Artículo',
};

const en: Dict = {
  announcement:
    'Free shipping in the eurozone over €50 (beauty & style only) · CARO’s personal selection',
  'announcement.fuel': 'Shipping includes a temporary fuel surcharge',
  'announcement.fuelMore': 'More about the shipping surcharge',
  'announcement.fuelInfo':
    'With rising fuel prices, carriers have raised their rates. For now, shipping carries a temporary surcharge, shown separately at checkout. Free shipping in the eurozone over €50 still applies. We will remove it as soon as transport prices return to normal.',

  'nav.shop': 'Shop',
  'nav.theEdit': 'Edit',
  'nav.journal': 'Edit',
  'nav.caro': 'CARO',
  'nav.instagram': 'Instagram',
  'nav.contact': 'Contact',
  'nav.beauty': 'Beauty',
  'nav.casa': 'Casa',
  'nav.estilo': 'Style',
  'nav.finds': 'Finds',
  'nav.all': 'All',
  'nav.about': 'About CARO',
  'nav.philosophy': 'Philosophy',
  'nav.services': 'Services',
  'nav.newsletter': 'A letter from CARO',

  'a11y.skip': 'Skip to content',
  'a11y.menuOpen': 'Open menu',
  'a11y.menuClose': 'Close menu',
  'a11y.cart': 'Cart',
  'a11y.breadcrumb': 'Breadcrumb',
  'a11y.homeLink': 'CARO. Casa de estilo, home',
  'a11y.mainNav': 'Main',
  'a11y.sectionsNav': 'Sections',
  'a11y.menu': 'Menu',
  'a11y.qtyDecrease': 'Decrease',
  'a11y.qtyIncrease': 'Increase',

  'common.addToCart': 'Add',
  'common.enquire': 'Enquire',
  'common.onRequest': 'On request',
  'common.priceTbc': 'Price to be confirmed',
  'common.unverified': 'Details unverified',
  'common.home': 'Home',
  'common.viewBeauty': 'View beauty',
  'common.discoverCasa': 'Discover Casa',
  'common.readMore': 'Read more',
  'common.loading': 'Loading…',
  'common.language': 'Language',
  'common.keepExploring': 'Keep exploring',
  'common.retry': 'Try again',
  'common.loadError': 'The page could not be loaded.',
  'common.backHome': 'Back to home',
  'common.name': 'Name',
  'common.email': 'Email',
  'common.message': 'Message',
  'common.sending': 'Sending…',
  'common.sendError': 'Could not send. Please try again.',
  'common.hours': 'Hours: Monday to Saturday, 10:00 to 17:00. Other times by appointment.',

  'states.errorTitle': 'Something went wrong',
  'states.errorMsg': 'Please try again in a moment.',

  'home.heroStatement': 'A curated house of style, beauty and wellbeing.',
  'home.ctaPrimary': 'Discover the selection',
  'home.ctaSecondary': 'Discover CARO',
  'home.closing.line1': 'Style for women who evolve.',
  'home.closing.line2': 'Beauty and wellbeing through Carolina’s eye.',
  'home.closing.signature': 'CARO SIGNATURE. My signature. Your choice.',
  'home.introEyebrow': 'CARO’s eye',
  'home.introTitle': 'Carolina has already chosen what deserves your attention.',
  'home.introBody':
    'CARO brings together style, beauty and wellbeing with one simple idea: the selection matters more than the catalogue. Every piece is here because Carolina would choose it for herself.',
  'home.introTodo': 'TODO: CONTENT — Carolina’s biography and signature pending.',
  'home.aboutLink': 'About CARO →',
  'home.editorialEyebrow': 'How we look at beauty',
  'home.editorialTitle': 'Fewer products. Better rituals.',
  'home.editorialBody':
    'We believe in simple, effective routines made for skin that evolves. Intentional selection, ingredients that deliver, and the pleasure of a daily gesture. No impossible promises.',
  'home.casaEyebrow': 'Casa',
  'home.casaTitle': 'Objects from CARO’s home',
  'home.casaLede': 'Collectible art and design. Selected pieces, often unique.',
  'home.preparing': 'Preparing the selection…',

  // Poster hero (editorial cover, replicates the printed portada)
  'home.poster.brandSub': 'Casa de estilo',
  'home.poster.tagline': 'Style · Beauty · Presence · Casa',
  'home.poster.title': 'Getting dressed is only the beginning.',
  'home.poster.subLead': 'For women who don’t want to follow everything, but to learn to choose ',
  'home.poster.subAccent': 'what truly represents them.',
  'home.poster.needTo': 'Know how to',
  'home.poster.choose': 'Choose',
  'home.poster.wear': 'Wear',
  'home.poster.be': 'Be',
  'home.poster.byLabel': 'By',
  'home.poster.byName': 'Carolina Collaer',
  'home.poster.closing': 'Your style can be seen. Your presence must be felt.',
  'home.about.eyebrow': 'Who is CARO',
  'home.about.title': 'Image consulting in Sitges, Garraf, Penedès and Barcelona',
  'home.about.body1':
    "I'm Carolina Collaer, {services|image consultant and personal shopper}. I help you choose what truly represents you, in person or online.",
  'home.about.body2':
    'You can also explore {shop|my online shop}, with exclusive selections in fashion, beauty and home, {instagram|follow me on Instagram} or stay in the loop with {letter|A letter from CARO}.',

  'ig.title': 'On Instagram',
  'ig.altFallback': 'Instagram post by Carolina Collaer',
  'testimonials.eyebrow': 'Trust',
  'testimonials.title': 'What they say',
  'testimonials.item': 'Testimonial',
  'testimonials.placeholder': '(placeholder)',

  'newsletter.eyebrow': 'Community',
  'newsletter.title': 'Receive the Letter from CARO.',
  'newsletter.body': 'Once a month, you will receive this letter with ideas, finds, what is coming soon to CARO and little secrets about style, beauty, presence and home. A letter to inspire you, discover and choose better.',
  'newsletter.placeholder': 'you@email.com',
  'newsletter.submit': 'Subscribe',
  'newsletter.sending': 'Sending…',
  'newsletter.done': 'Thank you. Check your inbox to confirm.',
  'newsletter.consent': 'I agree to receive what I have ticked and the privacy policy.',
  'newsletter.consentLetter': 'I agree to receive CARO’s letter and the privacy policy.',
  'newsletter.consentFinds': 'I agree to receive CARO’s Finds notices and the privacy policy.',
  'newsletter.listsLegend': 'What you would like to receive',
  'newsletter.list.letter': 'A letter from CARO · once a month',
  'newsletter.list.finds': 'CARO’s Finds · only when a drop arrives',
  'newsletter.listsError': 'Choose at least one: the letter or Finds.',
  'newsletter.error': 'Check the email and the consent box.',
  'newsletter.honeypot': 'Do not fill in',

  // /newsletter page (the shared Newsletter form sits below this copy)
  'newsletterPage.eyebrow': 'Community',
  'newsletterPage.title': 'Receive the Letter from CARO.',
  'newsletterPage.lede': 'Once a month, you will receive this letter with ideas, finds, what is coming soon to CARO and little secrets about style, beauty, presence and home.',
  'newsletterPage.p1': 'A letter to inspire you, discover and choose better.',
  'newsletterPage.p2': 'And if you love one-off pieces, sign up for Finds too: I only write when a drop arrives.',
  'newsletterPage.signoff': 'With love,',
  'newsletterPage.signature': 'Carolina Collaer',
  'newsletterPage.imageAlt': 'Carolina Collaer, founder of CARO. Casa de estilo',
  'newsletterPage.formTitle': 'Be part of CARO.',
  'newsletterPage.formBody': 'Leave me your email and confirm your subscription through the link you will receive.',
  'newsletterPage.unsubTitle': 'Want to unsubscribe?',
  'newsletterPage.unsubBody':
    'Every letter ends with a link to unsubscribe in one click, whenever you like. If you cannot find it, write to me and I will remove you myself.',
  'newsletterPage.unsubLink': 'Request to unsubscribe by email',
  'newsletterPage.unsubSubject': 'Unsubscribe from A letter from CARO',
  'newsletterPage.privacyLead': 'Your data is handled under our',
  'newsletterPage.privacyLink': 'privacy policy',
  'newsletterPage.archiveTitle': 'Past letters',
  'letter.formTitle': 'Would you like the next one?',
  'letter.readArticle': 'Read the article',
  'letter.notFoundTitle': 'Letter not found',
  'letter.notFoundMsg': 'This letter may not have been sent yet.',

  'cart.title': 'Your selection',
  'cart.empty': 'Your cart is empty.',
  'cart.subtotal': 'Subtotal',
  'cart.note': 'Shipping and taxes calculated at checkout.',
  'cart.checkout': 'Checkout',
  'cart.remove': 'Remove',
  'cart.size': 'Size',
  'cart.pageTitle': 'Cart',
  'cart.emptyTitle': 'Your cart is empty',
  'cart.emptyMsg': 'CARO’s selection awaits.',
  'cart.discoverBeauty': 'Discover beauty',

  'footer.explore': 'Explore',
  'footer.info': 'Information',
  'footer.tagline': 'Style for women who evolve.',
  'footer.tagline2': 'Beauty and wellbeing through Carolina’s eye.',
  'footer.signature': 'CARO SIGNATURE. My signature. Your choice.',
  'footer.prices': 'Prices in EUR, VAT included where applicable.',
  'footer.madeBy': 'Website by',
  'footer.legal.notice': 'Legal notice',
  'footer.legal.privacy': 'Privacy',
  'footer.legal.cookies': 'Cookies',
  'footer.legal.terms': 'Terms of sale',

  'shop.hubEyebrow': 'CARO selection',
  'shop.hubTitle': 'CARO’s selection',
  'shop.hubCta': 'Discover',
  'shop.hubIntro':
    'CARO’s personal selection: style, beauty and design objects chosen with judgement. And, to guide you, Carolina’s image consulting.',
  'shop.beauty.title': 'Beauty',
  'shop.beauty.eyebrow': 'Beauty · CARO selection',
  'shop.beauty.lede': 'Skincare selected for skin that evolves.',
  'shop.beauty.intro':
    '## Beauty with judgement.\n\n' +
    'Fewer promises. Better choices.\n\n' +
    'Beauty evolves with us too. Our skin changes, our needs change and, above all, the way we want to care for ourselves changes.\n\n' +
    'At CARO we see beauty as a conscious choice: knowing better what we need, learning to tell the essential from the superfluous, and surrounding ourselves with products that bring quality, pleasure and meaning to our everyday rituals.\n\n' +
    '## A few formulas, well chosen\n\n' +
    'This selection isn’t about piling up new launches or following the latest trend. It brings together serums, sun protection, eye cream and masks from brands such as Meisani and Ondo Beauty 36.5, chosen by Carolina to accompany skin that evolves.\n\n' +
    'On each product page you’ll find what it offers and how to use it, so you can decide with information and without rushing.\n\n' +
    '## This is also knowing how to choose\n\n' +
    'A routine doesn’t get better by having more steps. It gets better when every gesture makes sense to you and you keep it up consistently. In the Edit we talk about beauty from the point of view of image and care: how to organise a routine, which questions to ask yourself before buying and how to recognise yourself without trying to correct yourself.',
  'shop.casa.title': 'Casa',
  'shop.casa.eyebrow': 'Collectible art and design',
  'shop.casa.lede': 'Objects with personality, often unique pieces. Discover CARO’s world.',
  'shop.casa.intro':
    '## Your home speaks of you too.\n\n' +
    'The objects you choose, the art that keeps you company, a lamp, a book or a special piece build the identity of your home.\n\n' +
    'It isn’t about filling spaces, but about knowing how to choose.\n\n' +
    '## Pieces with a story\n\n' +
    'Casa brings together works and objects with personality, often unique pieces: NAOR’s original sculptures or Hamed Ouattara’s signature design, among others. Carolina chooses them for their vision, their craft and the story they tell, not for the trend of the moment.\n\n' +
    'They are pieces to live with for years, not to change every season. That is why they are worth choosing calmly.\n\n' +
    '## Before you decide\n\n' +
    'If a piece interests you and you’d rather ask before buying, on the page of many of them you can request information. We’ll get back to you personally.\n\n' +
    '## A home that looks like you\n\n' +
    'Every object you choose says something about the person who lives with it. In the Edit we talk about art, about design and about how to live with the pieces we choose.',
  'shop.estilo.title': 'Style',
  'shop.hallazgos.title': 'Finds',
  'shop.estilo.eyebrow': 'Style · CARO selection',
  'shop.estilo.lede': 'Fashion suggests. CARO selects. You decide.',
  'shop.estilo.intro':
    '## Here you’ll find what deserves to stay.\n\n' +
    'Because a good bag, glasses with presence, an impeccable jacket or the right accessory are not simply add-ons. They are decisions. And when they are well chosen, they build a coherent image with no need for excess.\n\n' +
    '## Fewer pieces, better chosen\n\n' +
    'In Style we bring together clothes and accessories that Carolina chooses with the question behind all of CARO: does it make sense for you? Knitwear, jackets, bags and natural-stone jewellery from labels such as Surkana, alongside the CARO bangle.\n\n' +
    'It isn’t about renewing your wardrobe every season, but about adding what speaks to what you already own and to who you are today.\n\n' +
    '## Knowing how to wear\n\n' +
    'A garment, a piece of jewellery or a colour never tells exactly the same story on two people. In the Edit we share ideas for wearing each piece your way: proportions, combinations and those small details that can transform an image.\n\n' +
    'Style appears where the reference ends and your interpretation begins.\n\n' +
    'Fashion suggests. CARO selects. You decide.',
  'shop.servicios.title': 'Image consulting',
  'shop.servicios.eyebrow': 'With Carolina',
  'shop.servicios.lede': 'Personal guidance on image, style and presence.',
  'shop.allTitle': 'All',
  'shop.allEyebrow': 'CARO selection',
  'shop.allLede': 'The complete CARO. Casa de estilo selection.',
  'shop.emptyTitle': 'Nothing here yet',
  'shop.emptyMsg': 'Come back soon. The selection is being prepared.',

  'finds.title': 'Finds',
  'finds.eyebrow': 'Style · One-off pieces',
  'finds.lede': 'What Carolina finds in shops, on her travels and in the sales. One of each.',
  'finds.intro':
    '## One of each\n\n' +
    'Every find is a piece Carolina has discovered and chosen for you: in a small shop, while travelling or on the last rail of the sales. There is no restock. When it’s gone, it’s gone.\n\n' +
    '## How she chooses\n\n' +
    'Visible quality, pieces that last and that speak to what you already own, chosen one by one.',
  'finds.newsletterCta': 'You can also receive A letter from CARO, once a month',
  'finds.letterEyebrow': 'Live drop alert',
  'finds.letterTitle': 'Know the moment a find arrives.',
  'finds.letterBody': 'I write as soon as a drop goes live, not later: one-off pieces, one of each, and the first to arrive takes it. No other emails.',
  'finds.emptyTitle': 'The next find is on its way',
  'finds.emptyMsg': 'Carolina is searching. Sign up for the Finds notices and I will write when the next drop arrives.',
  'finds.emptyCta': 'See all subscriptions',
  'finds.teaserEyebrow': 'Finds',
  'finds.teaserText': 'One-off pieces Carolina finds in shops, on her travels and in the sales. One of each.',
  'finds.teaserCta': 'See the finds',
  'finds.story': 'The story',
  'finds.measurements': 'Measurements',
  'finds.condition': 'Condition',
  'finds.condition.new_with_tags': 'New with tags',
  'finds.condition.new': 'New',
  'finds.condition.excellent': 'Excellent condition',
  'finds.condition.good': 'Good condition',

  'pdp.unverifiedLong': 'Details unverified. Official information pending',
  'pdp.addToSelection': 'Add to selection',
  'pdp.officialInfo': 'Product information',
  'pdp.spec.size': 'Size',
  'pdp.spec.benefits': 'Benefits',
  'pdp.spec.howToUse': 'How to use',
  'pdp.spec.ingredients': 'Ingredients',
  'pdp.spec.inci': 'INCI',
  'pdp.spec.edition': 'Edition',
  'pdp.disclaimer':
    'Official ingredient and INCI information prevails over any editorial note. Not medical advice.',
  'pdp.notFoundTitle': 'Product not found',
  'pdp.notFoundMsg': 'This piece may no longer be available.',
  'pdp.enquireTitle': 'Request information',
  'pdp.enquireAlso': 'Prefer to ask before buying? Request information',
  'pdp.fuelShipping': 'Shipping with the temporary fuel surcharge:',
  'pdp.zoneSpain': 'Spain',
  'pdp.zoneEurope': 'Europe',
  'pdp.zoneInternational': 'International',
  'pdp.perPiece': ' (per piece).',
  'shipping.estimate.handling': 'Preparation {min}-{max} working days',
  'shipping.estimate.delivery': 'delivery:',
  'shipping.estimate.zone.es': 'Spain {min}-{max}',
  'shipping.estimate.zone.europe': 'Europe {min}-{max}',
  'shipping.estimate.zone.international': 'international {min}-{max}',
  'shipping.estimate.total': 'Estimated delivery: {min}-{max} working days',
  'pdp.enquireThanks': 'Thank you for your interest. We’ll get back to you personally as soon as possible.',
  'pdp.sendRequest': 'Send request',
  'pdp.soldOutTitle': 'Sold out',
  'pdp.soldPieceTitle': 'Sold',
  'pdp.soldPieceText':
    'This one-of-a-kind piece has already found its home. If you are looking for something with the same spirit, the selection keeps growing.',
  'pdp.soldPieceLetterTitle': 'Be the first to discover what arrives at CARO.',
  'pdp.soldFindLetterTitle': 'Don’t miss the next find.',
  'pdp.alternatives': 'See the {category} selection',
  'pdp.unavailable': 'Not available right now.',
  'restock.title': 'Let me know when it’s back',
  'restock.body': 'Leave your email and we will write to you only once, when it is available again.',
  'restock.submit': 'Notify me',
  'restock.consent': 'I agree to receive an email from CARO about this piece and accept the privacy policy.',
  'restock.alsoLetter': 'Also subscribe to A letter from CARO',
  'restock.done': 'Thank you. Check your inbox to confirm the alert.',
  'restock.error': 'Check the email and the consent box.',
  'pdp.available': 'In stock',
  'pdp.allInCart': 'You already have all available units in your selection.',
  'pdp.size': 'Size',
  'pdp.chooseSize': 'Choose your size to add it to your selection.',
  // Packs (docs/plan-packs.md)
  'pack.badge': 'Pack −{pct}%',
  'pack.includes': 'Includes:',
  'pack.contents': 'What it includes',
  'pack.savings': 'You save {amount}',
  'pack.unavailable': 'This pack is not available right now.',
  'pack.unavailableHint': 'You can still see each product on its own page.',
  'pack.sizeFor': 'Size for {name}',
  'pack.chooseSizes': 'Choose a size for each sized product.',
  'pack.notFoundTitle': 'Pack not found',
  'pack.notFoundMsg': 'This pack may no longer be available.',
  'pack.metaTitle': 'Pack',
  'checkout.packIssueTitle': 'A pack in your selection is not available',
  'checkout.packIssueBody': 'A product may have sold out or a size may be missing. Review the pack or remove it to continue.',
  'checkout.packIssueView': 'View the pack',
  'pdp.sizeSoldOut': 'Sold out',
  'pdp.preorderNotice':
    'On its way: this piece is being restocked, so delivery will take a little longer.',
  'product.preorderBadge': 'Restocking',
  'product.editTitle': 'In the Edit',
  'pdp.caroNote': 'Why CARO chose it',

  'checkout.eyebrow': 'Secure payment',
  'checkout.title': 'Complete order',
  'checkout.contact': 'Contact details',
  'checkout.phone': 'Phone (optional)',
  'checkout.email': 'Email (required)',
  'checkout.emailHint': 'We’ll send your confirmation and shipping updates.',
  'checkout.firstName': 'First name',
  'checkout.lastName': 'Last name',
  'checkout.shipping': 'Shipping',
  'checkout.address': 'Address',
  'checkout.city': 'City',
  'checkout.zip': 'Postal code',
  'checkout.province': 'Province',
  'checkout.country': 'Country',
  'checkout.pay': 'Pay with SumUp',
  'checkout.preparing': 'Preparing payment…',
  'checkout.secureNote':
    'Price, VAT, stock and totals are calculated securely on the server. Payment is processed by SumUp; CARO never stores card data.',
  'checkout.summary': 'Your selection',
  'checkout.shippingCalc': 'Shipping calculated next based on destination.',
  'checkout.vat': 'VAT included',
  'checkout.shippingLabel': 'Shipping',
  'checkout.total': 'Total',
  'checkout.free': 'Free',
  'checkout.fuelSurcharge': 'Temporary fuel surcharge',
  'checkout.calculating': 'Calculating…',
  'checkout.shippingPending': 'Calculated from your address',
  'checkout.shippingNote2': 'Shipping depends on the destination and is confirmed with your address.',
  'checkout.customsNote': 'Shipments outside the EU may be subject to customs duties and taxes, payable by the buyer.',
  'checkout.emptyTitle': 'Your cart is empty',
  'checkout.emptyMsg': 'Add a piece from the selection to continue.',
  'checkout.widgetNote':
    'Widget payment requires SumUp activation. Set SUMUP_CHECKOUT_MODE to "hosted" to test the full flow.',
  'checkout.genericError': 'Could not start the payment. Please try again.',
  'checkout.error.invalidRequest': 'Some order details are not valid.',
  'checkout.error.cartInvalid': 'Please review your cart.',
  'checkout.error.turnstile': 'Anti-robot check failed. Please reload the page and try again.',
  'checkout.manualTitle': 'We’ll handle your order by email',
  'checkout.manualBody':
    'We couldn’t verify your address automatically, so we’ll resolve your order personally by email. No payment has been taken and stock is not guaranteed until confirmed. Reference:',
  'checkout.addrSuggestTitle': 'Did you mean this address?',
  'checkout.addrSuggestBody': 'We found a similar address. Review it and apply if correct:',
  'checkout.addrUse': 'Use this address',
  'checkout.addrKeep': 'Keep mine and send as enquiry',
  'checkout.addrNotFoundTitle': 'We couldn’t find your address',
  'checkout.addrNotFoundBody': 'Please check it and try again, or send it as an enquiry for manual handling (no payment, stock not guaranteed).',
  'checkout.addrRetry': 'I’ve checked, retry',
  'checkout.addrSendManual': 'Send as enquiry',
  'checkout.address2': 'Optional address line 2 (floor, door, stair…)',
  'checkout.addrHelp': 'Confirm your address to ensure delivery before paying.',
  'checkout.validate': 'Validate address',
  'checkout.addrChecking': 'Checking…',
  'checkout.addrVerified': 'Address verified ✓',
  'checkout.addrNone': 'We couldn’t find this address. Check it or continue as an enquiry.',
  'checkout.addrChoose': 'Choose your address:',
  'checkout.addrCheckError': 'Couldn’t validate right now. You can continue anyway.',
  'checkout.continue': 'Continue',
  'checkout.reviewTitle': 'Review your order',
  'checkout.reviewEyebrow': 'Confirmation',
  'checkout.reviewIntro': 'Check that everything is correct before proceeding to payment.',
  'checkout.reviewContact': 'Contact',
  'checkout.reviewShipTo': 'Ship to',
  'checkout.reviewCost': 'Cost breakdown',
  'checkout.reviewNoPhone': 'No phone',
  'checkout.confirmPay': 'Confirm and pay',
  'checkout.editOrder': 'Back to edit',
  'checkout.acceptPre': 'I accept the',
  'checkout.acceptTermsLink': 'Terms of purchase',
  'checkout.acceptMid': 'and have read the',
  'checkout.acceptPrivacyLink': 'Privacy policy',
  'checkout.reviewShippingPending': 'Confirmed at payment',
  'checkout.soldOutTitle': 'So sorry, just sold out',
  'checkout.soldOutBody':
    'The last piece sold while you were finishing your order. We’re truly sorry for the timing.',
  'checkout.soldOutContact': 'Want us to let you know if it returns? Get in touch.',
  'checkout.soldOutBack': 'Back to the shop',
  'checkout.backorderTitle': 'Back-order: delayed delivery',
  'checkout.backorderBody':
    'These items are out of stock. Delivery may take longer than usual, normally 1 to 2 weeks. As the order ships together, the whole order would arrive with that delay.',
  'checkout.backorderContinue': 'Continue anyway',
  'checkout.backorderRemove': 'Remove',
  'checkout.sizeRequiredTitle': 'Choose a size',
  'checkout.sizeRequiredBody':
    'These pieces are now sold by size. Pick yours on the product page, or remove it from your cart to continue.',
  'checkout.sizeRequiredFor': 'Choose a size for',
  'checkout.sizeRequiredChoose': 'Choose size',

  'return.verifying': 'Confirming your payment…',
  'return.thanks': 'Thank you',
  'return.paidTitle': 'Order confirmed',
  'return.paidBody1': 'We’ve received your payment. Your order number is',
  'return.paidBody2': 'We’ll email your confirmation if you provided an address.',
  'return.pendingTitle': 'Payment being verified',
  'return.pendingBody1': 'Your payment is still being confirmed. Keep your order number',
  'return.pendingBody2': '; we’ll let you know once it completes.',
  'return.failedTitle': 'Payment not completed',
  'return.failedBody': 'No charge was made. You can try again.',
  'return.errorTitle': 'We couldn’t verify the order',
  'return.errorBody': 'If you believe the payment went through, contact us with your order number.',
  'return.contact': 'Contact',

  'about.eyebrow': 'About CARO',
  'about.portrait': 'Portrait of Carolina',
  'about.title': 'It all began with a question.',
  'about.lede':
    'Why do some people have presence even before they speak?',
  'about.story1':
    'I’m Carolina Collaer. For years I have worked around image, communication, art, fashion and design.',
  'about.story2':
    'Over time I understood that all those disciplines were, deep down, about something similar: how we choose to present ourselves to the world.',
  'about.story3':
    'CARO. Casa de estilo is born from that view. Not to tell you what to wear. To share what I have learned, keep learning with you and help you develop something far more interesting than a perfect wardrobe: your own criteria.',
  'about.tags': 'Image consultant · Fashion & luxury · Art & lifestyle · ES · FR · EN',
  'about.followIg': 'Shall we connect on Instagram?',

  'philosophy.title': 'Philosophy',
  'philosophy.lede': 'Style for the woman who evolves.',
  'philosophy.body':
    'We believe beauty, style, wellbeing and presence are one conversation. And that curation matters more than the catalogue.\n\n' +
    'It all began with a question: why do some people have presence even before they speak? CARO. Casa de estilo is born from that view. Not to tell you what to wear, but to help you develop something far more interesting than a perfect wardrobe: your own criteria.\n\n' +
    '## Knowing how to choose\n\n' +
    'Learning to tell apart what simply catches our eye from what truly represents us. Choosing better doesn’t mean having fewer options. It means having more judgement.\n\n' +
    '## Knowing how to wear\n\n' +
    'A garment, a fragrance, a piece of jewellery or a colour never tells exactly the same story on two people. Style appears where the reference ends and your interpretation begins.\n\n' +
    '## Knowing how to be\n\n' +
    'Presence is not built only from what can be seen. Image can catch the eye; behaviour is what ends up leaving a mark.\n\n' +
    '## Style · Beauty · Wellbeing\n\n' +
    'A house for the woman who evolves, through Carolina’s eye. Fashion suggests. CARO selects. You decide.\n\n' +
    'We do not set out to lay down universal rules or to tell you how you should be. We want to offer you references, knowledge and questions that let you observe better, question more and decide for yourself.\n\n' +
    'Because the more you know, the less you need to follow.\n\n' +
    '**KNOWING HOW TO CHOOSE · TO WEAR · TO BE**',

  'services.colorimetry.eyebrow': 'Free colour analysis',
  'services.colorimetry.text': 'Before you book, find your colour season from your hair, eyes and skin, take away your personalised PDF, free and anonymous, and play to check you understand your palette.',
  'services.colorimetry.cta': 'Find my colours',
  'services.colorimetry.imageAlt': 'COLORIMETRY ME!: fashion sketch dressed in seasonal colours with a sample of palettes',
  'services.silhouette.eyebrow': 'Free body shape quiz',
  'services.silhouette.title': 'Your silhouette',
  'services.silhouette.text': 'Answer 4 questions in front of the mirror, with no measurements or photos, find your silhouette and the pieces that balance it, take away your personalised PDF, free and anonymous, and play “Choose for the effect”.',
  'services.silhouette.cta': 'Take the body shape quiz',
  'services.silhouette.imageAlt': 'CARO body shape quiz: fashion sketch of an X silhouette',
  'services.face.eyebrow': 'Free face shape quiz',
  'services.face.title': 'Your face shape',
  'services.face.text': 'Answer 3 questions in front of the mirror, with no photos, find your face shape and the frames, hairstyles, accessories and contouring that suit it, take away your personalised PDF, free and anonymous, and try frames in the fitting room.',
  'services.face.cta': 'Take the face shape quiz',
  'services.face.imageAlt': 'CARO face shape quiz: sketch of an oval face in cat-eye glasses',
  'services.eyebrow': 'Image & Presence',
  'services.title': 'Image consulting and personal shopping in Sitges, Garraf and Penedès',
  'services.lede': 'Your image doesn’t start in the wardrobe.',
  'services.manifesto1': 'It starts by understanding who you are today, what you want to convey and what you really need.',
  'services.manifesto2':
    'CARO helps you look at your image with more clarity: recognising what suits you, understanding what you already have, building better combinations and learning to choose with more intention.',
  'services.manifesto3': 'It’s not about following trends, accumulating clothes or becoming someone else.',
  'services.manifesto4': 'It’s about your image making sense with you.',
  'services.mantra': 'Knowing how to choose. To wear. To be.',
  'services.imageAlt': 'Carolina, image consulting',

  // Where I work: the zones themselves come from SERVICE_AREA (config/site.ts).
  'services.area.eyebrow': 'Where I work',
  'services.area.body': 'In-person sessions in store, at your home or at the event venue.',
  'services.area.travel': 'Online from anywhere, and travel within Spain or abroad on request.',

  // Sub-tabs: personal services vs. brands/stores/groups.
  'services.tabs.label': 'Choose the type of consulting',
  'services.tabs.personal': 'For you',
  'services.tabs.pro': 'Brands, stores & groups',
  'services.tabs.proLede':
    'Image experiences for boutiques, brands, hotels, private events and groups.',

  // — For you —
  'services.svc.sesion.name': 'CARO Session · Image & Presence',
  'services.svc.sesion.price': '€300',
  'services.svc.sesion.meta': '4 hours',
  'services.svc.sesion.body1':
    'A deep, personal session, at your home or in the CARO space, working from your own wardrobe.',
  'services.svc.sesion.listTitle': 'We look at:',
  'services.svc.sesion.list':
    'colours and tones that work for you\n' +
    'proportions, silhouettes and volumes\n' +
    'personal style and life stage\n' +
    'pieces you already own and how to use them better\n' +
    'accessories, glasses and small gestures that transform a look\n' +
    'make-up, when relevant\n' +
    'what to keep, what to reconsider and what may really be missing',
  'services.svc.sesion.body2':
    'During the session we create new combinations with your own pieces and identify what could complete your wardrobe without buying for the sake of it.',
  'services.svc.sesion.body3':
    'Afterwards you’ll receive a personalised digital summary with the main conclusions, combinations and recommendations from the session.',
  'services.svc.sesion.note': 'Also available as a gift voucher.',

  'services.svc.esencial.name': 'Essential session',
  'services.svc.esencial.price': 'From €120',
  'services.svc.esencial.meta': '90 minutes',
  'services.svc.esencial.body1':
    'For when you don’t need to review your whole wardrobe, but you do want a concrete, professional perspective.',
  'services.svc.esencial.body2':
    'It can focus on style, colour, glasses, accessories, a new professional chapter, an event, an important purchase or simply on solving what isn’t quite working in your image today.',
  'services.svc.esencial.note': 'In person or online.',

  'services.svc.compras.name': 'Personal shopper · Shopping guidance',
  'services.svc.compras.price': 'From €180',
  'services.svc.compras.body1': 'Buying better starts before you walk into a store.',
  'services.svc.compras.body2':
    'We review beforehand what you need, what’s worth adding and what you should stop buying.',
  'services.svc.compras.body3':
    'Then I accompany you in a personalised selection of clothing, accessories or glasses, looking for pieces that work with your life and with what you already have, not just with the trend of the moment.',

  // — Brands, stores & groups —
  'services.svc.tienda.name': 'CARO in-store experience',
  'services.svc.tienda.price': 'From €300',
  'services.svc.tienda.meta': '4 hours',
  'services.svc.tienda.body1':
    'An image experience designed for boutiques, brands, hotels, private events and retail spaces.',
  'services.svc.tienda.body2':
    'We can create short colour, style, accessories, glasses or make-up sessions of around 20 minutes per participant.',
  'services.svc.tienda.body3':
    'A different way to spark conversation, experience and connection around the product.',
  'services.svc.tienda.note': 'The format adapts to the space, the audience and the goal of the event.',

  'services.svc.privadas.name': 'Private experiences',
  'services.svc.privadas.price': 'By format',
  'services.svc.privadas.body1':
    'Birthdays, gatherings with friends, company events or small groups.',
  'services.svc.privadas.body2':
    'We design an experience around image, style and presence for small groups.',
  'services.svc.privadas.note': 'Priced by format and number of participants.',

  'services.svc.medida.name': 'Bespoke',
  'services.svc.medida.price': 'Custom quote',
  'services.svc.medida.body1': 'Some moments don’t fit a formula.',
  'services.svc.medida.list':
    'A one-off wardrobe review.\n' +
    'Preparing your image for a professional change.\n' +
    'Finding the right glasses.\n' +
    'Building looks for a trip.\n' +
    'Guidance on an important purchase.\n' +
    'A video call to resolve a specific question.',
  'services.svc.medida.body2': 'Tell me what you need and we’ll design the right support.',

  'services.finePrint':
    'Travel outside the established area is quoted separately by distance. The terms and duration of each service are confirmed before booking.',
  'services.formTitle': 'Book your consultation',
  'services.formCta': 'Request information',
  'services.formThanks': 'Thank you. CARO will be in touch very soon to arrange your consultation.',

  // Service pages (/caro/servicios/<servicio>, docs/plan-tests-y-servicios.md): AI draft for Carolina.
  'services.page.eyebrow': 'Image consulting · Sitges, Garraf, Penedès and Barcelona',
  'services.page.includesTitle': 'What it includes',
  'services.page.forWhomTitle': 'Who it is for',
  'services.page.howTitle': 'How it works',
  'services.page.priceTitle': 'Price and duration',
  'services.page.testsTitle': 'Before you book, try the free quizzes',
  'services.page.testsText': 'A first look, free and anonymous, that we then confirm together in the session.',
  'services.page.othersTitle': 'Other services',
  'services.page.allServices': 'All image consulting services',
  'services.page.more': 'See the details',
  'services.testsLink': 'See the three free style quizzes',
  'services.page.sesion.metaTitle': 'Image consultation session in Sitges',
  'services.page.sesion.metaDescription': 'The 4-hour CARO session: colours, silhouette, wardrobe, glasses and make-up with Carolina Collaer, at your home in Sitges, Garraf, Penedès and Barcelona.',
  'services.page.sesion.h1': 'Image consultation session in Sitges, Garraf and Penedès',
  'services.page.sesion.intro': 'The full CARO session: four hours to look calmly at your colours, your silhouette, your style and your wardrobe, and create new combinations with what you already own.',
  'services.page.sesion.forWhom': 'You want to review your image in depth, not just one detail\nYour wardrobe is full and you still don’t know what to wear\nYou are at a moment of change, professional or personal\nYou want to buy less and better',
  'services.page.sesion.how': 'We analyse your colours, your proportions and your style\nWe create combinations with your own clothes and see what might be missing\nYou receive a digital summary with the conclusions and recommendations',
  'services.page.esencial.metaTitle': 'Image consulting online or in person',
  'services.page.esencial.metaDescription': 'A 90-minute essential session with Carolina Collaer: colour, style, glasses, accessories, an event or an important purchase. In person or online.',
  'services.page.esencial.h1': 'Essential image consultation, in person or online',
  'services.page.esencial.intro': 'Ninety minutes for a professional, focused view, when you don’t need to review your whole wardrobe.',
  'services.page.esencial.forWhom': 'You need a specific answer, not a full review\nYou have an event, a new professional stage or an important purchase\nYou want to settle a question about colour, glasses or accessories',
  'services.page.esencial.how': 'You choose the topic: style, colour, glasses, accessories, an event or a purchase\nWe meet for 90 minutes, in person or online\nYou leave with specific answers to use from the next day',
  'services.page.compras.metaTitle': 'Personal shopper in Sitges and Barcelona',
  'services.page.compras.metaDescription': 'Personal shopping with Carolina Collaer: we work out what you need, then I go with you to choose clothes, accessories or glasses that suit your life.',
  'services.page.compras.h1': 'Personal shopper in Sitges, Penedès and Barcelona',
  'services.page.compras.intro': 'Shopping better starts before you walk into a shop: first we see what you need, then I help you choose it.',
  'services.page.compras.forWhom': 'You are updating part of your wardrobe and don’t want to get it wrong\nYou buy a lot and wear little\nYou are looking for an important piece: a coat, a suit, a pair of glasses',
  'services.page.compras.how': 'We review what you need and what is not worth buying\nI go with you on a personal selection of clothes, accessories or glasses\nWe choose pieces that work with your life and with what you already own',
  'services.page.tienda.metaTitle': 'Image events for shops and brands',
  'services.page.tienda.metaDescription': 'The CARO in-store experience: short colour, style, glasses or make-up sessions for boutiques, brands, hotels and events, with Carolina Collaer.',
  'services.page.tienda.h1': 'Image experience for shops, brands and hotels',
  'services.page.tienda.intro': 'An image experience that sparks conversation and connection around your product, in your space.',
  'services.page.tienda.forWhom': 'Boutiques and brands that want to bring the product closer to their customers\nHotels and venues looking for a different activity\nPrivate and commercial events',
  'services.page.tienda.how': 'We adapt the format to the space, the audience and the goal\nShort sessions of about 20 minutes per guest: colour, style, accessories, glasses or make-up\nYour team and your customers experience the product differently',
  'services.page.privadas.metaTitle': 'Image experiences for groups',
  'services.page.privadas.metaDescription': 'Birthdays, get-togethers with friends or company events: an experience around image, style and presence for small groups, with Carolina Collaer.',
  'services.page.privadas.h1': 'Private image and style experiences for groups',
  'services.page.privadas.intro': 'An experience around image, style and presence, designed for your group and your occasion.',
  'services.page.privadas.forWhom': 'Birthdays and get-togethers with friends\nCompany events\nSmall groups looking for something different',
  'services.page.privadas.how': 'You tell us the occasion and how many of you there are\nWe design the experience for you\nThe price depends on the format and the number of guests',
  'services.page.medida.metaTitle': 'Bespoke image consulting',
  'services.page.medida.metaDescription': 'Bespoke image consulting with Carolina Collaer: a wardrobe review, a career change, new glasses, a trip or one specific question. I will send a quote.',
  'services.page.medida.h1': 'Bespoke image consulting',
  'services.page.medida.intro': 'Some moments don’t fit a formula: we design the support you need.',
  'services.page.medida.forWhom': 'A specific need that doesn’t fit the sessions\nA trip, a career change or new glasses\nA question that a video call can settle',
  'services.page.medida.how': 'You tell me what you need\nI suggest the support and a quote\nWe do it in person or online',

  'edit.eyebrow': 'Edit',
  'edit.lede': 'Notes on style, beauty and wellbeing.',
  'edit.imageAlt': 'Carolina Collaer in a beige trench coat and scarf',
  'journal.introLede': 'Look more closely to choose more wisely.',
  'journal.intro':
    '## Knowing how to choose\n\n' +
    'Learning to tell apart what simply catches our eye from what truly represents us.\n\n' +
    'We will talk about fashion, colour, beauty, skincare, accessories, fragrance, design, objects and trends, but always starting from one essential question: does it make sense for me?\n\n' +
    'Choosing better doesn’t mean having fewer options. It means having more judgement.\n\n' +
    '## Knowing how to wear\n\n' +
    'Because a garment, a fragrance, a piece of jewellery or a colour never tells exactly the same story on two people.\n\n' +
    'We want to understand proportions, codes, combinations and those small details that can transform an image.\n\n' +
    'But also something more important: learning to make what we wear our own, without becoming a copy of what we see.\n\n' +
    'Style appears where the reference ends and your interpretation begins.\n\n' +
    '## Knowing how to be\n\n' +
    'This is where a conversation that is fundamental to CARO begins.\n\n' +
    'Presence is not built only from what can be seen.\n\n' +
    'It is also in the way you enter a room, listen, converse, say thank you, reply, share what you know, respect others, recognise someone else’s talent or help another person shine.\n\n' +
    'We will talk about behaviour, manners, generosity, character, humility, culture and social codes, because we believe all of it is part of the image we project.\n\n' +
    'You can dress impeccably and still lack elegance.\n\n' +
    'Because image can catch the eye; behaviour is what ends up leaving a mark.\n\n' +
    'The Edit wants to open conversations about fashion and beauty, but also about age, identity, culture, art, design, relationships, presence and those everyday gestures that seem small and yet say so much about us.\n\n' +
    'We do not set out to lay down universal rules or to tell you how you should be.\n\n' +
    'We want to offer you references, experiences, knowledge and questions that let you observe better, question more and decide for yourself.\n\n' +
    'Because the more you know, the less you need to follow.\n\n' +
    'And the more judgement you develop, the freer you are to choose.\n\n' +
    '**KNOWING HOW TO CHOOSE · TO WEAR · TO BE**\n\n' +
    'Read · observe · learn · question · choose\n\n' +
    'Getting dressed is only the beginning.',
  'edit.emptyTitle': 'More coming soon',
  'edit.emptyMsg': 'The editorial is being prepared.',
  'edit.cat.estilo': 'Style',
  'edit.cat.belleza': 'Beauty',
  'edit.cat.bienestar': 'Wellbeing',
  'edit.cat.casa': 'Casa',
  'edit.cat.mujer-40': 'Women 40+',
  'edit.shopBeauty': 'Visit the beauty store',
  'edit.shopEstilo': 'Visit the style store',
  'edit.shopCasa': 'Visit the Casa store',
  'edit.services': 'See image consulting',
  'edit.guide': 'How to combine your season’s colours',
  'edit.catIntro.estilo':
    'Style isn’t about following trends: it’s about learning to choose what truly represents you. Clothes, accessories, colour and proportions, through Carolina’s eye.\n\n' +
    'Notes on knowing how to choose and how to wear, always starting from one essential question: does it make sense for me? How to bring in a seasonal piece, how to dress for the woman you are today and how to add without piling up. Because style appears where the reference ends and your interpretation begins.',
  'edit.catIntro.belleza':
    'Beauty isn’t about correcting yourself but about recognising yourself and knowing how to choose. We write from image and care, not from the doctor’s office.\n\n' +
    'Here you’ll find how to organise a routine with fewer, well-chosen products, which questions to ask yourself before buying and how to accompany skin that changes over the years. No impossible promises, no clichés: beauty with judgement, for the woman who evolves.',
  'edit.catIntro.bienestar':
    'Wellbeing without obsession: caring for yourself calmly, without chasing perfection or competing with anyone.\n\n' +
    'We talk about small everyday care and that way of being that is also part of the image we project. We don’t give medical advice or universal recipes: we share a serene view so you can choose what makes sense for you. Because for CARO, beauty, style, wellbeing and presence are one conversation.',
  'edit.catIntro.casa':
    'Your home speaks of you too. Decorating well isn’t about filling spaces, but about knowing what deserves to stay.\n\n' +
    'Here we talk about art, design and objects with personality, about the makers we select for Casa and about how to live with the pieces we choose. A lamp, a book or a sculpture also tell who you are. Because a home is an extension of style, and it is also where we learn to choose.',
  'edit.catIntro.mujer-40':
    'The years don’t take possibilities away: they help us choose better. 40+, an age to live with intention and without clichés.\n\n' +
    'Here we talk about style, beauty and presence for the woman who evolves: dressing no longer to look like someone else, but to recognise ourselves. Ideas for choosing with more judgement, wearing what represents us and being present at every stage with confidence and ease.',
  'article.notFound': 'Article not found',
  'article.productsTitle': 'CARO’s selection',
  'share.label': 'Share',
  'share.copy': 'Copy link',
  'share.copied': 'Link copied',
  'share.more': 'More…',

  'contact.eyebrow': 'Let’s talk',
  'contact.title': 'Contact',
  'contact.lede': 'For style enquiries, orders, press or collaborations.',
  'contact.send': 'Send message',
  'contact.thanks': 'Thank you for your message. We’ll reply personally.',
  'form.source.label': 'How did you hear about us? (optional)',
  'form.source.placeholder': 'Choose an option',
  'form.source.google': 'Google',
  'form.source.maps': 'Google Maps',
  'form.source.instagram': 'Instagram',
  'form.source.referral': 'Recommendation',
  'form.source.event': 'In-store event',
  'form.source.press': 'Press',
  'form.source.other': 'Other',
  'contact.alsoHere': 'Also here',
  'contact.emailLabel': 'Email:',
  'contact.instagramLabel': 'Instagram:',
  'contact.whatsappCta': 'Message me on WhatsApp',
  'contact.whatsappAria': 'Open WhatsApp to message CARO (opens in a new tab)',
  'contact.privacyResponsable': 'Controller:',
  'contact.privacyNote':
    'We process your name and email only to handle your enquiry (basis: your consent). We do not use them for advertising or share them for that purpose. You can exercise your rights of access, rectification, erasure and objection. More information in our',
  'contact.privacyLink': 'Privacy policy',
  'contact.legalTodo': 'TODO: CONTENT — company legal details pending.',

  'notfound.eyebrow': 'Error 404',
  'notfound.title': 'This page got lost',
  'notfound.body': 'The link may have changed. Let’s go back to the selection.',

  'consent.title': 'Cookies',
  'consent.body':
    'We use necessary cookies for the site to work and, with your permission, analytics cookies (Google Analytics) to improve the experience.',
  'consent.accept': 'Accept all',
  'consent.reject': 'Only necessary',
  'consent.more': 'Cookie policy',
  'consent.configure': 'Cookie settings',

  // Legal pages chrome (the documents themselves live in src/content/legal*.ts)
  'legal.metaTitle': 'Legal',
  'legal.eyebrow': 'Legal information',
  'legal.notFound': 'Document not found',
  'legal.draftBadge': 'Draft. Pending professional legal review',
  'legal.draftNote':
    'This document is a draft. The identifying details are real; the clauses must be reviewed and validated by a professional under Spanish and European Union law before final publication.',

  // <head> titles of non-indexed or loading pages (indexable pages: lib/seo/pages.ts)
  'meta.checkout.title': 'Checkout',
  'meta.checkoutReturn.title': 'Confirmation',
  'meta.notFound.title': 'Page not found',
  'meta.product.title': 'Product',
  'meta.article.title': 'Article',
};

export const DICTIONARY: Record<UILocale, Dict> = { es, en, fr };

/**
 * Pure lookup with the UI's fallback: the locale's string, else the Spanish
 * source, else the key itself. Used by useI18n().t and by the Worker's
 * prerendered #root (same text the page paints).
 */
export function translate(locale: UILocale, key: string): string {
  return DICTIONARY[locale][key] ?? DICTIONARY.es[key] ?? key;
}
